/* SPEC-145 — In-app guided walk-throughs (the demo-day script, as a button).
 *
 * ONE source for every tour, same philosophy as the view-registry: the tour
 * engine (components/tour/*) is generic; the demo script lives here as data.
 *
 * A step targets a real element on a real page via a `data-tour="<anchor>"`
 * attribute and narrates in planner/exec language. `route` is an EXISTING deep
 * link (path + optional ?tab=) — the engine navigates there before spotlighting.
 * A missing anchor (element not rendered, or the step's page/tab isn't in the
 * user's visible set) is SKIPPED gracefully — never a blank spotlight, never a
 * fabricated claim. Narration must therefore survive empty states: it states
 * what the screen honestly shows, or what a capability does, not a specific
 * number that may not be on screen.
 *
 * Honesty rule (reviewer checks this): every claim below is anchored to a
 * visible affordance. Where a beat depends on data that may be absent (an
 * expanded forecast row, a specific finding), the step skips rather than lie. */

import type { TourGlyphId } from '../components/tour/icons/tourGlyphData';

export interface TourStep {
  /** data-tour key to spotlight. Omit for a centered card with no spotlight. */
  anchor?: string;
  /** Existing deep link (path + optional ?tab=). The engine navigates here first. */
  route?: string;
  title: string;
  /** Narration — the approved demo copy. Rendered into an aria-live region. */
  body: string;
}

export interface Tour {
  id: string;
  /** SPEC-509: the B mark the picker shows (components/tour/icons/tourGlyphData.ts). Keyed by
   *  the tour's own id; `tourGlyphs.test.ts` asserts every tour has one and no two share one. */
  glyph: TourGlyphId;
  title: string;
  /** Who it's for — one line in the picker. */
  audience: string;
  /** Roles this tour is hidden from — needed because steps targeting '/' are
   *  visible to every role (the pageVisible '/' loophole), so a tour whose lens
   *  is role-routed (e.g. the exec Risk Snapshot at '/') must be gated here.
   *  Found by the SPEC-166 live 3-role run-through (16 Jul). */
  hiddenForRoles?: string[];
  steps: TourStep[];
}

/* ── Shared step definitions ──────────────────────────────────────────────
 * The Full Demo (⑥) reuses the individual tours' steps verbatim, so the copy
 * lives once. Reference these from both the focused tour and the condensed one. */

/* SPEC-462 — the left rail. Anchored to the rail itself, which exists on desktop only
 * (`md:`), so on a phone the step SKIPS rather than describe a strip that is not there
 * (review finding 4). Route '/' = visible to every role. Trust, Help and Admin are named
 * as conditional because the rail shows them only where the login can see those pages.
 * SPEC-535: the Methodology button became Trust — it opens Docs, first page of the Trust group. */
const APP_RAIL: TourStep = {
  route: '/',
  anchor: 'app-rail',
  title: 'The strip down the left edge',
  // SPEC-509: the SuryaIQ mark at the top is the way home (Overview); Alerts, the
  // walk-throughs and Feedback sit together at the foot, above your account. No "magnifier"
  // wording: the rail draws its own marks now, and the search button is named in words.
  body:
    'This narrow strip holds the few things you need from anywhere. The SuryaIQ mark at the ' +
    'top takes you to Overview. Below it: show or hide the menu, page search and your ' +
    'decisions — plus Trust (the docs, methods and data checks), Help and Admin where you ' +
    'have them. At the foot, above ' +
    'your account: Alerts, the guided walk-throughs and Feedback. The strip stays put when ' +
    'you hide the menu, and the bar along the bottom of the screen says whether the system ' +
    'is healthy and when it last synced.',
};

/* SPEC-509 — the walk-throughs button at the foot of the rail. Desktop only (the rail is
 * `md:`), so on a phone the step SKIPS; the phone menu has a labelled row instead. */
const RAIL_TOURS: TourStep = {
  route: '/',
  anchor: 'rail-tours',
  title: 'Every walk-through, one button',
  body:
    'The compass at the foot of the left strip opens this list of guided walk-throughs. ' +
    'Each one drives the real screens; pick one any time, and a part-walked one offers to ' +
    'resume where you stopped.',
};

/* SPEC-509 — the Feedback button at the foot of the rail. The copy promises only what the
 * button does: it opens the feedback form. The form itself is SPEC-506's to describe. */
const RAIL_FEEDBACK: TourStep = {
  route: '/',
  anchor: 'rail-feedback',
  title: 'Tell us what you think',
  body:
    'The speech bubble at the foot of the left strip opens the feedback form, from any ' +
    'screen — for something that looks wrong, a question or an idea.',
};

/* SPEC-566 — the account control at the foot of the rail. Anchored to the trigger (the panel is
 * closed until opened), desktop only (the rail is `md:`), so on a phone the step SKIPS. The copy
 * names only the actions the panel always holds; the admin view picker is extra for admins.
 * SPEC-563 (merged after SPEC-566): the panel also always holds "Arrange menu", so the copy names it. */
const RAIL_ACCOUNT: TourStep = {
  route: '/',
  anchor: 'rail-account',
  title: 'Your account',
  body:
    'Your account sits at the very foot of the left strip. Open it to arrange your menu, change ' +
    'your password, sign out, or sign out of all devices at once. Use that last one if you left yourself ' +
    'signed in on another computer: every sign-in ends, this one included.',
};

/* SPEC-452 — the menu itself. Anchored to the sidebar search box, which is rendered only
 * while the sidebar is expanded, so the step SKIPS on a collapsed sidebar rather than
 * spotlighting an invisible element. Route '/' = visible to every role.
 * SPEC-462: the anchor deliberately stays on the BOX, not the rail's magnifier — this copy
 * says "type here", and the rail has its own step (APP_RAIL) just before this one. */
const NAV_FIND_PAGE: TourStep = {
  route: '/',
  anchor: 'nav-search',
  title: 'Find any screen by name',
  body:
    // SPEC-563: a login can arrange its own menu, so "is grouped by" was false for a custom
    // layout. "Starts grouped" is true for every layout (no layout = the standard menu).
    'The menu starts grouped by what you are doing — Plan, Buy, Sell, Money, Trust — and ' +
    'you can arrange it your own way. ' +
    // SPEC-459: the group labels are now orange, and the sections inside a screen use the
    // same colour. Stating the rule once here is what makes the colour learnable.
    // Honesty rule (SPEC-145) — review finding 2 killed the first draft of this sentence
    // ("an orange name is always a heading, never a screen you can open"). It was false:
    // the row you are ON is also orange. The TRUE discriminators were the small capitals
    // for a heading and the highlight for your location (until SPEC-535, below).
    // SPEC-509: no colour word — the heading colour is one token Param may flip between
    // yellow and orange, and the copy must stay true either way. Each heading carries a mark.
    // SPEC-535: the menu headings are no longer small capitals — they are the LARGEST text in
    // the menu, and the sizes step down as you go in. Only the section labels inside an
    // open screen are still small capitals. The arrow claim is true on every row that has tabs.
    // SPEC-563: only the standard groups carry a mark; a folder you make or rename has none.
    'Each group name is the largest line in the menu (the standard groups carry a mark); screens sit one step ' +
    'in, and a screen\'s sections one step further. A screen with an arrow at the end of its ' +
    'row opens into sections: the arrow shows or hides them, the name opens the screen. ' +
    'The screen you are on is the highlighted row. ' +
    'Type here to jump straight to a screen, by its name or by the word you would use for ' +
    'it ("oos", "amendments", "rebate"), and press Enter to open the best match. Hover a ' +
    'screen in the menu and use its star to pin it to Favourites, at the top. Open a ' +
    'screen to pin one of its sections the same way.',
};

/* SPEC-563 — arranging the menu. Anchored to the pencil beside the search box, which renders
 * only on the DESKTOP sidebar while it is expanded (the phone menu is read-only, D10), so the
 * step SKIPS on a phone or a collapsed sidebar rather than describe a button that is not there.
 * No step is anchored inside the Arrange dialog: a closed dialog's anchor always skips. */
const NAV_CUSTOMISE: TourStep = {
  route: '/',
  anchor: 'nav-customise',
  title: 'Arrange the menu your way',
  body:
    'Press the pencil beside the search box to arrange the menu. Each screen and group then ' +
    'shows a "…" button: move a screen up, down or into another folder, give a section of a ' +
    'screen (such as a Demand Planning tab) its own row in a folder, hide a screen you do ' +
    'not use, or make, rename and delete folders of your own. With a mouse you can also drag ' +
    'a screen or a folder to a new place. A new screen you have not filed ' +
    'yet shows under New at the top. A hidden screen is still found by search, and Arrange ' +
    'menu in your account menu lists what you have hidden, with Show, and lets you save a ' +
    'layout to come back to. Arranging changes what you see in the menu, never what you can ' +
    'open. Press the pencil again when you are done.',
};

/* SPEC-503 — the help button on the left rail. Anchored to the button itself, which the rail
 * renders only where the login can see the Docs page and only on desktop (the rail is `md:`),
 * so the step SKIPS elsewhere rather than describe a button that is not there. */
const HELP_BUTTON: TourStep = {
  route: '/',
  anchor: 'docs-help',
  title: 'Help for the screen you are on',
  body:
    'The question-mark button on the left edge opens the guide for the screen you are looking ' +
    'at — on any screen. Where a screen has no written guide yet, it opens that screen\'s entry ' +
    'in the list of every screen instead.',
};

/* SPEC-506 — the Feedback pill. Anchored to the pill itself, which every logged-in page
 * renders (the app shell mounts it once). The copy claims only what the pill and the panel
 * it opens show: the snip step, the four choices, and My feedback. No promise of an outcome. */
const FEEDBACK_PILL: TourStep = {
  route: '/',
  anchor: 'feedback-pill',
  title: 'Tell us when something looks wrong',
  body:
    'The Feedback button at the bottom right is on every screen. ' +
    'Press it and it takes a picture of the screen; drag a box round the part that matters, or ' +
    'use the whole page. Then pick one of four — something looks wrong, something is broken, a ' +
    'question, or an idea — and write one sentence. You can follow what happens to each one ' +
    'under My feedback.',
};

/* SPEC-503 — the Docs page. Three steps, each anchored to something always rendered once the
 * guides load: the header, the guide list with its search box, and the badge under a guide's
 * title. The badge step names both states because the reader may land on either. */
const DOCS_PAGE: TourStep = {
  route: '/docs',
  anchor: 'docs-page',
  title: 'How the platform works, in your words',
  body:
    'This page explains each screen and the ideas behind the numbers. It opens on the ' +
    'Getting started guide written for you. Some guides show the idea as a figure: a diagram, ' +
    'numbered steps, or a chart read live from the platform.',
};

const DOCS_NAV: TourStep = {
  route: '/docs',
  anchor: 'docs-nav',
  title: 'Find a guide',
  body:
    'Guides are grouped as Getting started, How-to guides, Concepts and Reference. Type in the ' +
    'search box to find a guide by any word in it.',
};

const DOCS_VERIFIED: TourStep = {
  route: '/docs',
  anchor: 'docs-verified',
  title: 'When each guide was last checked',
  body:
    'A written guide shows the day it was last checked against its screens. It turns amber, ' +
    'saying it needs re-verification, when that check is old. Reference pages built from the ' +
    'app itself say so instead, and some show a Live now strip with the values in use today.',
};

/* SPEC-549: the short films strip on the Docs page. It renders only when films are published,
 * so the step skips otherwise. */
const DOCS_WATCH: TourStep = {
  route: '/docs',
  anchor: 'docs-watch',
  title: 'Watch a one-minute film',
  body:
    'Short narrated films walk through one screen each, made from the screens themselves. Each ' +
    'card shows who it is for and how long it runs. A film made before its screen changed says ' +
    'so, and the guide stays the reference.',
};

/* SPEC-526: the watch line is conditional (nothing shows when all is clear), so the step
   describes it with "when", never as always on screen. */
const MORNING_VIEW: TourStep = {
  route: '/demand-planning?tab=morning',
  anchor: 'morning-view',
  title: 'The morning loop',
  body:
    'One screen answers the three questions you start the day with: is the data fresh, ' +
    'what needs me today, and how is the week scoring. When something needs watching, a ' +
    'line at the top says what and why: amber to watch, red only when it needs action now. ' +
    'Every card here is one click from acting on it.',
};

const THIS_WEEK: TourStep = {
  route: '/demand-planning?tab=morning',
  anchor: 'this-week',
  title: 'This week, in four numbers',
  body:
    "Locked cases, SKUs covered, the actuals score (the date this week was last scored as real " +
    "orders land — it keeps accruing), and last week's accuracy (WAPE) — the week's plan at a " +
    'glance, scored against real actuals as they come in.',
};

/* SPEC-453: every data screen now carries THIS ribbon, in this wording, in this position —
 * and the ⓘ beside it is the new capability the step has to name, because a hover is not
 * discoverable on its own. Param's ask, verbatim: "people ask what it actually means". */
const FRESHNESS: TourStep = {
  route: '/demand-planning?tab=morning',
  anchor: 'freshness',
  title: 'Every number says where it came from',
  body:
    'This ribbon shows how old the data behind the screen is, and it is on every screen in ' +
    'the same words and the same place. If a feed goes stale it turns red and says so, ' +
    'rather than showing a confident wrong number. The small ⓘ beside it opens the ' +
    'plain-English meaning of the term — the same wording the assistant uses, so the two can ' +
    'never tell you different things. You will find the same ⓘ on the grey status labels and ' +
    'headline figures on the promotions screens, where it also gives the specific reason ' +
    'behind that row when one was recorded. On the planning screens it also says when the ' +
    'forecast was last published and when the next weekly publication is due.',
};

/* SPEC-410 P2: the unified at-a-glance chip next to the page title — same freshness
 * verdict as FRESHNESS above, one level more compact. Renders on Demand Planning, the
 * Material Planning Console and Stock Targets. */
const DATA_SOURCE_INDICATOR: TourStep = {
  route: '/demand-planning?tab=morning',
  anchor: 'data-source-indicator',
  title: 'Live, or last-good?',
  body:
    'This chip is the one-glance verdict. A calm "Live data" means every critical feed is ' +
    'current. "Showing held data — as of {date}" means one has gone stale. Same source as ' +
    'the strip above, always on, so you never wonder which mode you’re in.',
};

const WORKLIST: TourStep = {
  route: '/demand-planning?tab=morning',
  anchor: 'worklist',
  title: 'What needs me today',
  body:
    "Amendments about to lapse, POs waiting on a decision, the biggest projected deficits, " +
    "today's exceptions — each says why it's here and drills straight to the action.",
};

const DEMAND_REQUESTS: TourStep = {
  route: '/demand-planning?tab=demand-requests',
  anchor: 'demand-requests',
  title: 'Sales asks, you decide — and it is tracked',
  body:
    'When sales asks for a volume — "300K of 12620 for September" — it lands here as a tracked ' +
    'request, not a lost email. You accept, amend the number, or decline, each with a reason. ' +
    'An accepted volume is spread across the period\'s weeks and shown as a NAM comparison line ' +
    'against the forecast — it never silently changes the plan. Who asked, what you decided and ' +
    'why is kept as an audit on both sides. A count on the Demand Planning menu shows how many ' +
    'requests are still waiting on you, so nothing sits here unseen.',
};

const FORECAST_DRILL: TourStep = {
  route: '/demand-planning?tab=forecast&view=accuracy',
  anchor: 'forecast-drill',
  title: 'The forecast, in your language',
  body:
    'Every SKU carries its champion model, its accuracy (WAPE) and its bias — in your own ' +
    'v6.4 terms. Expand any row for the curve, the tournament that model won, and a docked ' +
    'form to amend it right where you read it — logged with a reason, held in force until the ' +
    'week you amended has passed, and ' +
    'scored for value added: did your call beat the model? A change due to lapse asks you to ' +
    'keep or amend it first — nothing is removed without telling you. Any week that overlaps a promotion ' +
    'on the tracker carries a "P" — on past actuals so a spike can be read against the promotion that was running, and on ' +
    'current/upcoming weeks. Hover a "P" week to see the customer, the promo period, and the ' +
    'expected and actual quantities for the promotion period. It also shows what the promotion ' +
    'would add, marked "not applied" — the forecast stays unchanged until that is agreed. Where ' +
    'the tracker has no dates or promotion quantities, the week says so ' +
    'instead of showing a zero. Use the download button ' +
    'by the horizon selector to take the whole series — history and forecast at your chosen ' +
    'horizon — straight into Excel.',
};

// SPEC-532 (Sudhin, 08 Oct 2026): the final forecast for every line, week by week. The view is
// inside the Forecast tab; the step skips while the list is still loading (no anchor yet).
// Every Forecast-tab step names `view` (accuracy | final): the engine navigates only on a param a
// step NAMES, so a step without it would leave the other view on screen (test_tour_coverage).
const FINAL_FORECAST_EVERY_LINE: TourStep = {
  route: '/demand-planning?tab=forecast&view=final',
  anchor: 'dp-final-forecast',
  title: 'The final forecast for every line',
  body:
    'This view lists every line in the plan, week by week — the final forecast the plan runs ' +
    'on, added up across customers. The line above the table says how many lines, weeks and ' +
    'customers it holds, and which published book it reads. Search by code or name, filter by ' +
    'demand class and sort by total. A dash means nothing is published for that week, not a ' +
    'zero. The download button saves exactly the lines shown, every week, as one file.',
};

// SPEC-536 (Sudhin, 08 Oct 2026): click a line in the every-line view to open it. The anchor is
// the opened panel, so the step skips while no line is open — it never claims a panel that is
// not on screen.
const FINAL_FORECAST_LINE_DETAIL: TourStep = {
  route: '/demand-planning?tab=forecast&view=final',
  anchor: 'dp-final-line-detail',
  title: 'Open a line: customers, basis, signals and why',
  body:
    'Click a product code to open the line under its row. "Why" is one plain sentence from the ' +
    'forecast explanation. "How many weeks" says how far ahead this line is forecast and how many ' +
    'weeks the published book holds for it. Below are what the number rests on, the signals on the ' +
    'line, and its customers week by week, with a download of this line by customer.',
};

// SPEC-545 (Param, 11 Oct 2026): the team's own weekly forecast, uploaded and set beside the platform
// figure. The card renders under the every-line list, so the step skips while that view is not open.
const FINAL_FORECAST_TEAM_UPLOAD: TourStep = {
  route: '/demand-planning?tab=forecast&view=final',
  anchor: 'dp-team-forecast',
  title: "Set the team's weekly forecast beside the platform",
  body:
    "Upload the team's weekly forecast file (product code, week, units). Each upload is kept as a new " +
    'version. Every line is compared with the platform figure for the same product and week as it stood ' +
    'when you uploaded. The table shows, week by week, how many lines were close and the middle gap, ' +
    'and the line above it says whether the two moved closer. A week with no upload says so.',
};

// SPEC-548 (Param, 11 Oct 2026): the purchasing system's forecast, the platform's and the team's,
// against what sold. The card renders on the "Three forecasts" view; the step skips while it loads.
const THREE_FORECASTS: TourStep = {
  route: '/demand-planning?tab=forecast&view=three',
  anchor: 'dp-three-forecasts',
  title: 'Three forecasts against what sold',
  body:
    "Each completed week sets three figures beside what sold: the platform's forecast as published, " +
    "the team's upload and the purchasing system's weekly average (a trailing average of past sales, " +
    "not a plan), with the platform model's figure before amendments " +
    'scored beside them. Each is the figure that stood before the week began. The line at the top ' +
    'names the last scored week, on how many products all three had a figure and how often the ' +
    'platform came closest. The table shows what sold, and WAPE and bias for each source with the ' +
    'number of products behind each and its forecast total beside what sold. A source with no figure ' +
    'says so and is left out, never counted as zero. Select a week to see it like for like, by ' +
    'category and product by product.',
};

const THREE_FORECASTS_PRODUCTS: TourStep = {
  route: '/demand-planning?tab=forecast&view=three',
  anchor: 'dp-three-forecasts-products',
  title: 'Product by product, with the closest marked',
  body:
    'The week picked above shows every product here: what sold and the three forecasts, with the ' +
    'closest ticked and a short note on why the figures differ. Products the purchasing system has ' +
    'at zero are hidden by default; the line above the table says how many, and a switch shows them. ' +
    'Filter by category, demand class or a missing source, and download all 13 weeks as one file. ' +
    'Weeks before the purchasing system was recorded read "not recorded yet". Click a product code ' +
    'to open the line on the final forecast.',
};

// SPEC-553: the coverage line under the picked week.
const THREE_FORECASTS_OUTSIDE: TourStep = {
  route: '/demand-planning?tab=forecast&view=three',
  anchor: 'dp-three-forecasts-outside',
  title: 'What sold with no platform forecast',
  body:
    'For the picked week: the units and pounds sold on products the platform had no forecast for, ' +
    'grouped by why: a status outside planning, a by-product, a product that had not sold before the ' +
    'forecast was made, no rate of sale, no sale in the 12 weeks before, or not filed. Pounds are the ' +
    "units at the week's average order price. The link shows those products in the table below, each " +
    'with its reason.',
};

// SPEC-590 — the forecast "With promotions" view (Sudhin, 23 Oct 2026).
const WITH_PROMOTIONS: TourStep = {
  route: '/demand-planning?tab=forecast&view=promotions',
  anchor: 'dp-with-promotions',
  title: 'The forecast with promotions',
  body:
    'Every product with a promotion in the plan\'s weeks, week by week. Each week adds the ' +
    "promotion figure to the published base to give the engine figure. Each promotion sits on the " +
    "weeks its orders land (from the dispatch date, the in-store date or the retailer's order lead), " +
    'and the hover names which. Where you amended a week, ' +
    "your figure is shown instead (blue), with the engine figure beside it. These promotion " +
    'figures sit beside the plan and are not in it until the promotion is approved. A dash means ' +
    'no figure, never zero; hover a week for the reason. Click a product code to see each week ' +
    'made up and the promotions behind it. Below, each finished promotion shows what the engine ' +
    "predicted before it started, the rep's figure and what was ordered.",
};

// SPEC-595 — the with-promotions figure scored against what sold (Param, 27 Oct 2026). The card
// renders below the tracking table once the view has loaded; while absent the step skips.
const WITH_PROMOTIONS_ACCURACY: TourStep = {
  route: '/demand-planning?tab=forecast&view=promotions',
  anchor: 'dp-with-promotions-accuracy',
  title: 'How the with-promotions figure scored',
  body:
    'Each product a promotion was added onto is scored the way the Forecast accuracy page scores ' +
    'the forecast: WAPE and bias over finished weeks, for the base and for the base plus the ' +
    'promotion figure on the same weeks. The card says how many products came out better, worse or ' +
    'about the same, by demand class and by retailer, and names the products where promotions made ' +
    'it worse. The live record is saved every night and scored once a week ends; the back-test ' +
    "rebuilds earlier weeks as of each week's Monday, from what was known then. Every figure says " +
    'which record it comes from. Open a product to see what sold against both lines.',
};

// SPEC-595 — the pair on the accuracy list (renders with the list; skips while absent).
const ACCURACY_WITH_PROMOTIONS: TourStep = {
  route: '/demand-planning?tab=forecast&view=accuracy',
  anchor: 'accuracy-with-promotions',
  title: 'Accuracy with promotions',
  body:
    'On products with promotion volume, two more columns give the with-promotions WAPE and bias, ' +
    "beside the base's on the same weeks, and this line gives the median both ways. Green means " +
    'adding the promotion figure brought it closer to what sold; red means further. It informs ' +
    'only: nothing here changes what plans from the forecast.',
};

const WHO_WAS_CLOSER_TILE: TourStep = {
  route: '/',
  anchor: 'overview-who-was-closer',
  title: 'Who was closer',
  body:
    "For the last scored week, named on the tile: on the products that had all three figures — the " +
    "platform's forecast, the team's and the purchasing system's weekly average — how often the platform's was closest " +
    'to what sold. The count of products is always shown. When no product had all three, the tile ' +
    'says why instead of a number.',
};

const FORECAST_GRID_SCOPE: TourStep = {
  route: '/demand-planning?tab=forecast&view=accuracy',
  anchor: 'dp-grid-scope',
  title: 'Choose what the forecast grid download holds',
  body:
    'The forecast grid download now starts on all lines. Switch it to the planner set when you ' +
    'want only those. Each choice shows how many lines it holds, and the file itself says ' +
    'which scope it is.',
};

// SPEC-520 (G76): the two accuracy measures. The line only renders once the accuracy list has
// loaded; while it is absent the step skips rather than pointing at nothing.
const ACCURACY_TWO_MEASURES: TourStep = {
  route: '/demand-planning?tab=forecast&view=accuracy',
  anchor: 'accuracy-two-measures',
  title: 'Two measures of accuracy',
  body:
    'Each product now shows two accuracy figures side by side. Test WAPE is the back-test score ' +
    'from the model tournament. Actual WAPE is the forecast we filed for each of the recent ' +
    'completed weeks (the column header says how many) against what was then ordered. A product plans from its forecast when ' +
    'either one is inside the band for its demand class; only when both are outside is it marked ' +
    'for the rate-of-sale safeguard. The "Plans from" column says which measure passed. With ' +
    'too few scored weeks, or nothing ordered in the window, the actual record is not yet ' +
    'available and the test score decides alone. This line counts each group and says whether ' +
    'the safeguard is switched on.',
};

// SPEC-588: two yardsticks beside the bands — last year's same week (the naive forecast) and the
// planner's earlier forecasts. The line renders with the accuracy table; while it is absent the
// step skips rather than pointing at nothing.
const ACCURACY_BENCHMARKS: TourStep = {
  route: '/demand-planning?tab=forecast&view=accuracy',
  anchor: 'accuracy-benchmarks',
  title: 'Compared with what?',
  body:
    "The band says how close a forecast was; this line says whether it beat something simpler. " +
    "The naive forecast is what the product sold in the same week last year (or the average of " +
    "the 13 weeks before, when last year's week cannot be used). Your earlier forecasts are the " +
    "figures you made before the platform, once the file is uploaded. Both are scored on the same " +
    "weeks and the same sold figures as the actual WAPE. The two columns give each product's lead " +
    "in WAPE points: 'Beats naive by 17 pts' means last year's figure missed by 17 points more. " +
    "These comparisons inform only; the bands still decide what plans from the forecast.",
};

const FORECAST_STOCK: TourStep = {
  route: '/demand-planning?tab=forecast&view=accuracy',
  anchor: 'forecast-current-stock',
  title: 'Stock on hand, right on the forecast',
  body:
    'Beside the forecast sits the current stock on hand and its weeks of cover — the same ' +
    'figure Stock Cover shows, so you no longer flick between the two pages to line them up. ' +
    'If a product is missing from the live warehouse feed, the tile says so plainly — it ' +
    'shows the purchasing system’s stock report figure when there is one, or, when the feed only came back part ' +
    'way and this product was not in it, it marks the stock as unknown rather than a false ' +
    'zero. Never a silent count you can’t trust. ' +
    'Hover the tile to break the figure down: available plus internal transfers, what is on ' +
    'hold, allocated, and expired — the same breakdown Stock Cover shows, so the numbers ' +
    'line up. Until the fuller warehouse feed lands, it says that breakdown is still to come ' +
    'rather than guessing. Any recent site-to-site transfers for the product show underneath ' +
    'for context — they are shown for information only, never counted as incoming stock.',
};

const FORECAST_REGIME: TourStep = {
  route: '/demand-planning?tab=forecast&view=accuracy',
  anchor: 'regime-chip',
  title: 'What state is this product in?',
  body:
    'Expanded, each SKU now shows its regime — one plain word for the situation it is in: ' +
    'steady, a promo episode, a decline watch (demand falling away), a new-product ramp, or in ' +
    'transition after its pattern changed. Hover the chip for the reason. It is read-only for ' +
    'now — a lens over signals you already have — and the place we connect the guardrails, model ' +
    'choice and review rules to as you shape what each state should do.',
};

const FORECAST_PERIOD_AMEND: TourStep = {
  route: '/demand-planning?tab=forecast&view=accuracy',
  anchor: 'forecast-override-period',
  title: 'Amend a whole promotion in one go',
  body:
    'A promotion runs for weeks — so switch "Add override" to "A period", pick the run of weeks ' +
    'by their week-commencing dates, and set the uplift once. It pins to those calendar weeks ' +
    'and each week\'s change lapses as that week passes — no re-entering the same number week ' +
    'after week. Remove the whole period in one click too.',
};

const FORECAST_CONTROLS: TourStep = {
  route: '/demand-planning?tab=forecast&view=accuracy',
  anchor: 'forecast-controls',
  title: 'Re-run or guide one product',
  body:
    'Re-run only the expanded product, then choose from the models that its current test actually tried. ' +
    'A model choice needs a reason and expiry, and the normal tournament takes over again when it lapses. ' +
    'For a new product, you can also lock a valid similar product and re-run this product only. The latest ' +
    'run result, choice, reason, owner and expiry stay visible here.',
};

// SPEC-517 (G62): the forecast is published once a week, after the week's orders are in, and
// every weekly version is kept. The panel sits in the expanded row, so the step skips when no
// row is expanded; with no publication recorded yet the panel says so (narration survives it).
const FORECAST_HISTORY: TourStep = {
  route: '/demand-planning?tab=forecast&view=accuracy',
  anchor: 'forecast-history',
  title: 'The forecast changes once a week — and every week is kept',
  body:
    'The forecast is published once a week, after the week\'s orders are in, so the numbers do ' +
    'not move from one day to the next. Open "Past publications" in an expanded product to see ' +
    'how a week\'s forecast moved from one weekly publication to the next: when it was ' +
    'published, the week of orders it rested on, the week its model\'s data ends, the forecast, ' +
    'its base, any promotion, other signals, the demand class and the agreed plan\'s number. Until the first weekly publication ' +
    'is recorded for a product, the panel says so rather than showing zeros.',
};

const FORECAST_WORKING_SET: TourStep = {
  route: '/demand-planning?tab=overview',
  anchor: 'working-set-funnel',
  title: 'Not every product — the working set',
  body:
    'The forecast is not run on everything the business sells. This funnel shows the narrowing: ' +
    'from the full catalogue, drop the delisted lines and by-products, keep the SKUs with enough ' +
    'clean order history to be given a demand class and a fitted forecast, down to the curated ' +
    'planner set we measure. Hover any stage to see exactly what it counts. Every screen and the ' +
    'assistant read this same funnel, so the counts never disagree.',
};

const FORECAST_CLASSIFICATION: TourStep = {
  route: '/demand-planning?tab=classification',
  anchor: 'demand-classification',
  title: 'Every SKU knows what kind of demand it has',
  body:
    'Before anything is forecast, each SKU is sorted by how it actually sells — smooth, erratic, ' +
    'intermittent, lumpy, or a new product without enough history yet. The class decides which ' +
    'models are worth trying for it and which accuracy target it is held to. Click any class to ' +
    'filter the list to just those SKUs. The All / Produced / Imported switch sits under the page ' +
    'title; while it is on, a chip there shows it — and any list it would empty tells you how many SKUs are hidden, ' +
    'with a Show anyway button, so a filtered-out SKU is never a silent blank.',
};

const FORECAST_LOCK_SCORE: TourStep = {
  route: '/demand-planning?tab=forecast-vs-actuals',
  anchor: 'forecast-vs-actuals',
  title: 'Locked when the week starts, scored when it ends',
  body:
    'Each week the forecast is filed and locked as the week begins, then scored against what ' +
    'actually happened once the week closes — shown here as the forecast at the time (dotted) ' +
    'against the actuals (solid), with the buy decision recorded that week. Nothing is recomputed ' +
    'after the fact: you are reading the record as it stood. A week still in progress shows as ' +
    'accruing rather than pretending a final score. A completed week with no orders counts as zero ' +
    'demand — not left as still-pending — so a forecast filed for something that then sold nothing ' +
    'is scored as the miss it was; delisted products are set aside so dead lines can\'t flatter the number.',
};

/* SPEC-270 (G42): the fill-rate/service card on the same Forecast-vs-Actuals tab. Anchored to
 * the card, which only renders once the fill-rate data loads, so this step skips gracefully
 * when the signal is absent (SPEC-145 honesty). The body states the honesty caveat plainly. */
const SERVICE_FILL_RATE: TourStep = {
  route: '/demand-planning?tab=forecast-vs-actuals',
  anchor: 'service-fill-rate',
  title: 'Service — did we pick what was ordered',
  body:
    'Your 98.5% service target now has a real measurement: of the units ordered, how many the ' +
    'warehouse picked, read by delivery week. Over-pick is capped per line, so the figure is ' +
    'never above 100%. It is honest about pick timing: recent weeks are still being picked, so a ' +
    'week only counts once its picking is complete — those weeks read "not yet measurable" rather ' +
    'than showing a made-up shortfall while picking is under way. This is the units-based version; ' +
    'a value-weighted view comes later.',
};

/* SPEC-594 (Sudhin, 27 Oct): the buying decisions, marked up beside the forecast measures. The
 * strip renders on every load (a named state before the first outcome), so the step never lands
 * on a blank spotlight. */
const FVA_BUYING_DECISIONS: TourStep = {
  route: '/demand-planning?tab=forecast-vs-actuals',
  anchor: 'fva-buying-decisions',
  title: 'Were the buying decisions right?',
  body:
    'Each buy suggestion is judged once its window has passed: right, wrong or mixed, from the ' +
    'daily stock record. Beside it, from the customer order book over the same window: the ' +
    'service level (cases dispatched out of cases ordered) and the lost sales (cases not ' +
    'dispatched, at the price of record). Until the first window ends, the strip gives the date ' +
    'the first outcomes are due. Open Recommendations and outcomes for every decision.',
};

const REGIME_MEASUREMENT: TourStep = {
  route: '/demand-planning?tab=pilot',
  anchor: 'regime-measurement',
  title: 'Attaching rules to the states — and measuring first',
  body:
    'You asked us to connect the states and measure what buying decision would have happened ' +
    'before anything automates. Here it is: each night we work out what the buy would have been ' +
    'if the state\'s rule applied — a promo state buys a little more cover, a decline state lets a ' +
    'dying line run down — and show it next to today\'s number. Nothing here changes a real buy. ' +
    'It is measurement only, and it is early days, so the picture sharpens as more weeks build up.',
};

const PILOT_SCORECARD: TourStep = {
  route: '/demand-planning?tab=pilot',
  anchor: 'pilot-scorecard',
  title: 'How the planner set is scoring',
  body:
    'The weekly scorecard, tile by tile. The ⓘ on each tile explains what it measures, how ' +
    'close it is to green, and why — no metric is a black box. Below it, select a product or a ' +
    'customer in the planner set tables and its detail opens in the side panel.',
};

const BBE_RISK: TourStep = {
  route: '/demand-planning?tab=bbe-risk',
  anchor: 'bbe-risk',
  title: 'What is going out of date, and where',
  body:
    'Stock at risk by shelf life — expired, under 60 days, under 90 days (short dated), plus what ' +
    'is held on QC. When the live feed carries near-dated batches these fill in per site; when it ' +
    'does not, the banner says so plainly and shows the purchasing system\'s own figure rather than a ' +
    'blank. "Under MLOR" waits on a minimum-life-on-reserve value the system does not hold yet — ' +
    'it is named honestly, not faked. Select an expired or short-dated line to open its batch, ' +
    'pallet and location in the side panel.',
};

/* SPEC-466: the agents' findings moved off the sidebar accordion onto their own page,
 * /alerts. These steps walk that page: the area tabs, the list, and Agent health. */
// Codex fix loop 1 (SPEC-145): the badge sentence states config/shell.alertsBadge exactly —
// the critical count outranks an updates failure, and only the two badge counts drive it.
const ALERTS_TABS: TourStep = {
  route: '/alerts',
  anchor: 'alerts-tabs',
  title: 'What needs a look, by area',
  body:
    'Alerts gathers what the watchers found, sorted into Planning, Buying, Sales, Money and Data. ' +
    'Your own area opens first — or Updates, when only updates are waiting. All shows ' +
    'everything, and Updates lists system notices. The Alerts button at the foot of the left ' +
    'strip shows one badge: first, a red number for critical alerts in your area that nobody ' +
    'has looked at; otherwise an amber dot when updates are unread; and "!" when it cannot ' +
    'tell — the critical count did not load, or there are no critical alerts and the updates ' +
    'count did not load.',
};

/* SPEC-509 — the Updates tab: the list the top-right bell used to drop down. Anchored to the
 * tab's panel, which renders in every state (loading, failed, empty, a list), so the copy
 * names what the panel always shows and promises no particular notice. */
const ALERTS_UPDATES: TourStep = {
  route: '/alerts?tab=updates',
  anchor: 'alerts-updates',
  title: 'Updates: notices from the system',
  body:
    'Updates lists the newest notices — a data run finished or failed, a forecast drifted, a ' +
    'guardrail tripped. Unread ones are labelled "unread": select one to mark it read, or use ' +
    'Mark all read when it appears. If the list cannot load, it says so instead of showing ' +
    'nothing.',
};

const AGENTS_PANEL: TourStep = {
  route: '/alerts',
  anchor: 'alerts-list',
  title: 'Watchers over the live data',
  body:
    'These agents run for real against the live data — nothing here is staged. Each alert says ' +
    'which watcher found it, when it was first seen, and the date of the data behind it. The ' +
    'amber "Open in" button takes you to the screen where you can act; when no screen exists ' +
    'for it yet, the alert says so. Acknowledge it once you have looked, or dismiss it.',
};

/* SPEC-484 (G72): the £ on each alert, with its kind of money named. */
const ALERTS_MONEY: TourStep = {
  route: '/alerts',
  anchor: 'alerts-money',
  title: 'One £ per alert, and what kind it is',
  body:
    'Within each severity, alerts are ordered by their £, largest first. The label under the ' +
    'figure says what kind of money it is: sales at risk before the next delivery, cash tied up, ' +
    'margin lost, or stock value at risk. These are never added together. The ⓘ lists every ' +
    'figure behind it and the date of that data. When something is missing, such as a lead ' +
    'time or a selling price, the alert says "£ not known" and why — never £0.',
};

/* SPEC-491: dismiss with a reason, the accuracy thumbs, and (admin) what happened. */
const ALERTS_DISMISS: TourStep = {
  route: '/alerts',
  anchor: 'alerts-dismiss',
  title: 'Dismiss it, and say why',
  body:
    'Dismiss asks for one reason: not relevant, already handled, wrong figure, or will revisit. ' +
    'The reason is kept on the alert\'s history with your name — open an alert to see it, or ' +
    'open "Dismissed alerts" below the list. Acknowledge, dismiss and "Open in" are all recorded.',
};

const ALERTS_THUMBS: TourStep = {
  route: '/alerts',
  anchor: 'alerts-thumbs',
  title: 'Flag an alert that looks wrong',
  body:
    'If an alert does not look right, given the data, press the thumbs-down to flag it. There is ' +
    'no thumbs-up: an alert that looks right needs nothing from you. Your flag is kept for the ' +
    'weekly review and read by a person. It changes no alert, no limit and no ranking.',
};

const ALERTS_SCOREBOARD: TourStep = {
  route: '/alerts',
  anchor: 'alerts-scoreboard',
  title: 'What happened to the alerts',
  body:
    'For each watcher: how many alerts it raised, how many were acknowledged, opened from here, ' +
    'or dismissed (and why), and the £ on the ones opened versus dismissed — one kind of money ' +
    'at a time, never added across kinds. A watcher nobody has answered says so.',
};

const PRIORITY_TOP_VALUE: TourStep = {
  route: '/',
  anchor: 'priority-top-value',
  title: 'The biggest £ on the alerts',
  body:
    'Priority Actions now opens with up to three alerts that carry a £, in the Alerts page\'s ' +
    'own order: most urgent first, then the largest £. Each names its kind of money and its data ' +
    'date. They are listed, never added up. When no alert carries a £, this part does not show.',
};

const AGENTS_ARMED: TourStep = {
  route: '/alerts',
  anchor: 'alerts-health',
  title: 'It tells you what it is waiting for',
  body:
    'Agent health lists every watcher and what its last run did. If a feed falls behind, a ' +
    'watcher pauses and says it is waiting rather than guessing; a failed or skipped run says so. ' +
    'An empty list is never read as "all clear" off a run that did not happen.',
};

const ASK_AGENTS: TourStep = {
  route: '/',
  anchor: 'ask-suryaiq',
  title: 'Ask the assistant',
  body:
    'Ask "what did the agents find today?" and the copilot answers from these same findings — ' +
    'cited, and dated to when each agent last ran.',
};

const REPORTS_STALENESS: TourStep = {
  route: '/reports',
  anchor: 'reports-staleness',
  title: 'Honesty up front',
  body:
    'Before any answer, Reports tells you where the underlying source lags live trading. It ' +
    'never hides the data date — being honest about freshness is the feature, not a footnote.',
};

// SPEC-480: the weekly leadership PDF. The card shows only to logins that can build and
// download packs; for everyone else the anchor is absent and the step skips itself.
const REPORTS_PACK: TourStep = {
  route: '/reports',
  anchor: 'reports-pack',
  title: 'The weekly leadership report',
  body:
    'A short PDF of the week, built every Monday morning or on request, from the same figures ' +
    'as these screens. Every number shows where it came from and how recent it is, and anything ' +
    'not yet measurable is named rather than shown as zero.',
};

// SPEC-555: the weekly reports kept week by week. Only logins that can download packs see the
// tab; for everyone else the anchors are absent and both steps skip themselves.
const REPORTS_EXEC_WEEKS: TourStep = {
  route: '/reports?tab=executive',
  anchor: 'reports-exec-weeks',
  title: 'Every week\'s report, kept',
  body:
    'Executive reports lists one row per week. The report built on Monday morning is the week\'s ' +
    'report of record: download it, or every week at once as a zip. A week without one says why.',
};

const REPORTS_EXEC_TRENDS: TourStep = {
  route: '/reports?tab=executive',
  anchor: 'reports-exec-trends',
  title: 'The headline figures over time',
  body:
    'Each headline figure across the last 13, 26 or 52 weeks, as the reports printed it, with a ' +
    'sentence on what moved most since the last report. A week with no report is a break in the ' +
    'line, never filled in.',
};

const REPORTS_NLQ: TourStep = {
  route: '/reports',
  anchor: 'reports-nlq',
  title: 'A real analyst call',
  body:
    'Ask in plain English — "Revenue by channel this year". It answers from the same tools as ' +
    'the assistant, cites the data it used and its date, and refuses what the data cannot ' +
    'support rather than guessing.',
};

// SPEC-523: the order margin card. The step describes every state so it never claims a figure
// the card is not showing. SPEC-556: it names the two margins.
const REPORTS_ORDER_MARGIN: TourStep = {
  route: '/reports',
  anchor: 'reports-order-margin',
  title: 'Margin on orders, two ways',
  body:
    'Two margins on each order. "After landed cost" is what it sells for less the landed cost. ' +
    '"Delivered" also takes off promotion rebates, the storage, picking and delivery costs and the order price adjustment; ' +
    'it shows only where all of those are known, with the share of lines in small print, and ' +
    'otherwise names what is missing. Until order cost arrives, the card says so and shows no figure.',
};

const REPORTS_DOWNLOAD: TourStep = {
  route: '/reports',
  anchor: 'reports-download',
  title: 'Every export is stamped',
  body:
    'Download to CSV or PNG and the file carries its source and date — a number that leaves the ' +
    'platform still says where it came from.',
};

const REPORTS_SAVED: TourStep = {
  route: '/reports',
  anchor: 'reports-saved',
  title: 'Saved reports',
  body: 'Keep the questions you ask often and rerun them any time against the latest data.',
};

const ROUTE_ORDER_POOL: TourStep = {
  route: '/routes?tab=order-pool',
  anchor: 'route-order-pool',
  title: 'Live orders, grouped to deliver',
  body:
    'The open order pool, grouped by postcode delivery area with stock-readiness — a starting ' +
    'point for route clusters. This is live order data, not a sample; the banner is explicit ' +
    'that sequencing and drive times are still to come.',
};

const ROUTE_MAP: TourStep = {
  route: '/routes?tab=live-map',
  anchor: 'route-map',
  title: 'On the map',
  body:
    'The same clusters plotted by postcode-area centroid — where the demand is, and how ready ' +
    'the stock is to fill it. (Live vehicle tracking is a later phase.)',
};

const ROUTE_LIVE: TourStep = {
  route: '/routes?tab=live-routes',
  anchor: 'routes-live',
  title: 'Live routes — waiting on fleet details',
  body:
    'No routes are shown here yet, on purpose. Planned routes appear once the vehicle fleet and ' +
    "each customer's delivery constraints are supplied. Until then this tab says so rather " +
    'than showing example routes.',
};

const ROUTE_READINESS: TourStep = {
  route: '/routes?tab=delivery-readiness',
  anchor: 'route-readiness',
  title: 'Ready to ship — and what Phase 2 needs',
  body:
    'Order fulfilment at a glance: what is ready, partial, or awaiting stock, line by line. ' +
    'The next phase adds auto-planned routes and drive times — and it needs two things from ' +
    "you: the fleet list and each customer's delivery constraints. That is the ask.",
};

/* SPEC-542 — Market news: an overview per story, and earlier weeks kept. The feed anchor
 * renders only when there are stories; the weeks anchor only once an earlier week is stored.
 * Either step SKIPS when its anchor is absent. */
const MARKET_NEWS_FEED: TourStep = {
  route: '/intelligence?tab=news',
  anchor: 'market-news-feed',
  title: 'Market news, with an overview of each story',
  body:
    'Each card is a news story about our commodities, freight or the UK food market. Open a ' +
    'card to read a one-line note on what it means for us, then a short overview of the ' +
    'story: what happened, why it matters to us, what it touches and what to watch. A line ' +
    'under the overview says what it was written from. The source link at the bottom opens ' +
    'the story in a new tab.',
};

const MARKET_NEWS_WEEKS: TourStep = {
  route: '/intelligence?tab=news',
  anchor: 'market-news-weeks',
  title: 'Look back week by week',
  body:
    'Earlier weeks are kept. Use Earlier and Later, or pick a week from the list, to read the ' +
    'stories stored that week, with the overviews as they were written then. This week shows ' +
    'what the news feeds return now, plus the stories stored earlier this week.',
};

/* SPEC-448: this step moved to /methodology with its tab, and dropped the word "pilot" —
   the platform is operational software, not a pilot. The phase framework itself is
   unchanged. SPEC-526: shown as "Adoption phases"; the four phase names stay his own. */
const METHOD_PHASE_SIGNOFF: TourStep = {
  route: '/methodology?tab=phase_signoff',
  anchor: 'method-phase-signoff',
  title: 'Adoption phases: your own sign-off framework',
  body:
    "Sudhin's four phases, live — each with the evidence for signing it off. A criterion " +
    'the platform does not use by design reads Not in use, not a failure. Below them, the ' +
    'watch conditions show clear, watch or act now, against limits still to be set with you.',
};

/* SPEC-448: the map now carries a health state on every box, so the narration says what
   the colours mean. The copy describes the MECHANISM, so it is true whether today's map is
   green, amber or grey — and it names the grey state honestly, because a stage the
   platform does not measure is the one thing this screen must never paint green. */
const GOV_DATA_FLOW: TourStep = {
  route: '/governance?tab=data_flow',
  anchor: 'gov-data-flow',
  title: 'How the data flows, end to end — with its health',
  body:
    'A plain-language map from where each number comes in — customer orders, stock, supplier ' +
    'orders — through to the screen you read it on. Every box carries its own state: reporting ' +
    'normally, worth a look, needs attention, or grey where the platform measures nothing there ' +
    'and says so. Tap a box for what arrived, what the stage did, what is wrong, who owns the ' +
    'fix, and which screens it affects — on a wide screen that opens in the panel on the right, ' +
    'otherwise beside the map. Switch to the grid to scan every stage at once.',
};

/* SPEC-463 — the right context panel. Anchored to the docked panel itself, which exists only
 * on a screen wide enough to hold it beside the page, so on a phone or a narrow window the
 * step SKIPS rather than describe a panel that is not there. The copy holds whether the panel
 * is open or folded to its strip: it names the button and the shortcut, not a state. */
const CONTEXT_PANEL: TourStep = {
  route: '/governance?tab=data_flow',
  anchor: 'context-panel',
  title: 'The panel on the right: what you selected, explained',
  body:
    'On a wide screen, this panel on the right edge explains what you select. Here it holds ' +
    'the evidence for the stage you tap on the map. Fold it to a thin strip with the button in ' +
    'its top corner, open it again from the strip, or press the ] key. Each page remembers ' +
    'your choice.',
};

const GOV_READINESS: TourStep = {
  route: '/governance?tab=readiness',
  anchor: 'gov-readiness',
  title: 'Where each screen stands',
  body:
    'Every screen, honestly labelled: which real feeds it leans on, whether those are live, ' +
    'and — for anything not yet open — what it is waiting for. It is why a screen can be shown ' +
    'to everyone while the screen itself says how far along it is. Nothing is marked confirmed ' +
    'until the feed behind it has been checked against the source system.',
};

const GOV_FRESHNESS: TourStep = {
  route: '/governance?tab=freshness',
  anchor: 'gov-freshness',
  title: 'Freshness, table by table',
  body:
    'The age of every source feeding the platform, in one place — so a stale number can always ' +
    'be traced to a stale feed.',
};

/* SPEC-214: the warehouse-feed-coverage card on the same freshness tab. Anchored
 * to the card, which only carries the data-tour attribute when the signal is live,
 * so this step skips gracefully in the not-applicable state (SPEC-145 honesty).
 * SPEC-241: the card's own explainer is two-state (it flips once the six-quantity
 * feed lands and the meaning of "absence" changes). This narration is written to be
 * true in BOTH states — it describes the mechanism and points at the card's own line
 * for which reading currently applies — so no step ever claims something off-screen. */
const GOV_WAREHOUSE_GAP: TourStep = {
  route: '/governance?tab=freshness',
  anchor: 'gov-warehouse-gap',
  title: 'Which products are on the purchasing-system fallback',
  body:
    'When a product has real demand but no rows in the live warehouse feed, its stock is read ' +
    'from the purchasing-system report instead. Today the feed leaves out products with zero or only-expired ' +
    'stock, so a listed product usually just has nothing usable in stock. Once the feed is ' +
    'upgraded to also list zero and expired stock, that flips — a product missing here will then ' +
    'mean nothing is recorded at all — and the card tells you which reading applies. Either way, ' +
    'the one to watch for is a listed product whose purchasing-system figure is not zero: a genuine feed gap.',
};

/* SPEC-303: the data-integrity sentinel card at the top of the same freshness tab.
 * Anchored to the card, which only carries the data-tour attribute once a sweep has a
 * verdict, so this step skips gracefully in the "no sweep yet" state (SPEC-145 honesty).
 * The narration describes the mechanism, true whether the current verdict is pass or
 * breach — it never claims a specific alert is on screen. */
const GOV_SENTINELS: TourStep = {
  route: '/governance?tab=freshness',
  anchor: 'gov-sentinels',
  title: 'When the data itself goes wrong',
  body:
    'These checks watch the data behind every stock decision — stock read from the purchasing-system report ' +
    'instead of the live feed, quantities that do not add up, or an overnight jump too big to be ' +
    'real. If one fails, it says so here in plain terms and tells you what to trust in the ' +
    'meantime, so a silent data problem can never quietly skew a stock decision.',
};

// SPEC-448: moved to /methodology with the Policy tabs.
const GOV_GUARDRAILS: TourStep = {
  route: '/methodology?tab=guardrails',
  anchor: 'gov-guardrails',
  title: 'Every policy number lives here',
  body:
    'Thresholds, floors, caps — every governance number sits here, not buried in code. When a ' +
    'ruling lands it is a switch flip on this screen, not a code change.',
};

// SPEC-448: moved to /methodology with the Audit tabs.
const GOV_AUDIT: TourStep = {
  route: '/methodology?tab=audit',
  anchor: 'gov-audit',
  title: 'The audit trail',
  body: 'Who changed what, and when. Every override and decision is recorded and reviewable.',
};

/* SPEC-486: the Changelog tab. Anchored to the tab body, which renders once the changelog
 * has loaded — so on a read failure the step skips rather than pointing at an error. The
 * copy names what is always on screen (months, plain-words entries, the per-site line);
 * it never promises a particular change is live. */
const METHOD_CHANGELOG: TourStep = {
  route: '/methodology?tab=changelog',
  anchor: 'method-changelog',
  title: 'What changed, and is it live here',
  body:
    'Every finished piece of work, in plain words, grouped by month, week and day. Each entry ' +
    'says whether it has reached the site you are on, and only says live once that is proven. ' +
    'When work is under way, a strip at the top lists it.',
};

/* ── Team Buying Loop (SPEC-166 — the supply-chain team's day) ──────────────
 * Working language: what needs ordering, what is waiting approval, where the
 * stock is, what is short-dated. Honesty rule holds — the ERP-comparison and the
 * 'investigate' marker are described as "where loaded", so the copy is true
 * whether or not the ERP benchmark feed is present; every anchor is a real,
 * always-rendered element on a tab the team role can see. No pilot/accuracy
 * vocabulary — that is the planner's and exec's screens, not this loop. */

const TEAM_MORNING: TourStep = {
  route: '/demand-planning?tab=morning',
  anchor: 'morning-view',
  title: 'Start of the day',
  body:
    'One screen to open the day on: how fresh the data is, what is waiting on a decision, and ' +
    'the biggest projected shortfalls. Every card drills straight to the job — no hunting ' +
    'across tabs.',
};

const TEAM_WORKLIST: TourStep = {
  route: '/demand-planning?tab=morning',
  anchor: 'worklist',
  title: 'What is waiting on you',
  body:
    'POs waiting for a decision, the biggest projected shortfalls, and today\'s exceptions — ' +
    'each row says why it is here and takes you straight to it. Nothing to remember; the list ' +
    'is the queue.',
};

/* SPEC-231: Planning & Schedule, Netting and Replenishment are now ONE console
 * (Sudhin's v6.5 ruling). Honesty rule (SPEC-145): this step claims only what is
 * actually on screen. v1 renders engine outputs that exist today; the action
 * ladder is live; shelf-life (SPEC-558) and economic optimisation (SPEC-554) are built
 * behind ruling switches and say so; auto-execution is never pretended. Every buy still
 * goes to PO approval for a person. */
const TEAM_PSR_CONSOLE: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-console',
  title: 'One console: product table, projection, recommendation',
  body:
    'The old Planning & Schedule, Netting and Replenishment tabs are now one screen. It opens on a ' +
    'table of the products in the worklist, ranked by priority and filterable by state (planner ' +
    'review, exception, no action). Nothing is opened for you. Click a product and its row expands ' +
    'in place to show its weekly inventory projection ' +
    '(opening + PO − forecast = closing) — all quantities in cases — with a forward-cover column, ' +
    'the time fence that says how soon you can act, the engine\'s buy recommendation with its order ' +
    'value and latest safe order week, and the reasoning trail. Above the projection is a planning ' +
    'header: lead time, current stock, a horizon selector, ' +
    'and what you will need versus what you will have over that horizon. The horizon selector sets ' +
    'how far the projection table reads before it dims the later weeks — a shortfall past the ' +
    'selected horizon stays visible, never hidden. Its minimum is 1.25 × the lead time; longer ' +
    'options (up to 30 weeks) for seasonal buying and price-break lookahead appear only when the ' +
    'SKU\'s forecast reaches that far. Below the header a Cover ' +
    'strip states the same stock three ways so there is never an unexplained third number: ' +
    'current-stock cover (stock only, excludes inbound), average cover (adds the inbound POs), and ' +
    'forward cover (weeks to run-out — timing-aware, netting inbound). These three read the SKU\'s ' +
    'full projection window and do not follow the horizon selector, so they stay put as you change it. ' +
    'The Cover column is the closing stock divided by the forward run rate and extends beyond the ' +
    'filed weeks at that rate — so a SKU sitting on months of stock reads its true cover (e.g. 43 ' +
    'weeks) and is flagged for excess review, rather than being capped at the window. The table\'s ' +
    '"to run-out" column ' +
    'reads a related but different figure — weeks to run out from today, which also nets the ' +
    'scheduled inbound POs, so it can read higher than any single week in the Cover column when a PO ' +
    'lands mid-horizon. In the projection, the PO ' +
    'column shows what is coming in when: hover a figure for the PO number(s) and click to open it ' +
    'in PO approval; hover or click a forecast figure to see its source and open the forecast page. ' +
    'The header states the scope honestly — the inventory pool is total-UK, but the SKU scope is ' +
    'the planner set (a "Planner set scope · N SKUs" chip). At the top of the console a Cover criteria line ' +
    'shows the current bands (critical low, min–max and critical high, in weeks of cover); the ' +
    'planning lead can amend these target-driven values in place, and every change is recorded ' +
    'with who and when. Open "Workings" under the projection to see how ' +
    'each figure is built. The action ladder (delay/expedite/hold/cancel) now runs — it shows live ' +
    'advisory suggestions, each naming its PO. Shelf-life and economic optimisation are built but ' +
    'switched off until the planner rules on them, and each says so. There ' +
    'is no automatic ordering — every recommendation is actioned by a person through PO approval. ' +
    'If a planning input ever stops updating correctly — order data, stock levels or forecasts — a ' +
    'system-health banner appears at the top of this console (and the copilot says the same thing): ' +
    'the numbers are still shown, but the confidence is withdrawn so you verify before acting.',
};

// SPEC-292: the two first-class screens — Imported and Produced. A SCREEN switch, not a
// filter: each screen is a complete console, scoped on the server. Honest (SPEC-145) about
// where typing comes from and about untyped SKUs showing on both.
const TEAM_PSR_SCREENS: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-screen-tabs',
  title: 'Two screens: Imported and Produced',
  body:
    'The console has two screens — Imported and Produced — and you are always on one of them. ' +
    'Each is a full console in itself: the product table, the counts at the top (planner review, ' +
    'exception, actioned today), the sections and the export all belong to the screen you are ' +
    'on, worked out on the server, not a rough on-screen split. The screen you pick is kept in ' +
    'the page address, so a link opens the same screen — it is not a hidden setting. Which screen ' +
    'a SKU sits on comes from the type in the buying workbook (Imported or Produced). A SKU that ' +
    'has no type yet shows in a "Not typed" section on both screens, never dropped — so an ' +
    'un-typed SKU is a gap to close, not something hidden. If the other screen has an exception ' +
    'waiting, a small link at the top says so and takes you there, so nothing urgent is out of sight.',
};

// SPEC-294: the SKU scope switch — planner set / all SKUs / CS. Honest (SPEC-145) about what each
// scope shows: the planner forecast set, every SKU with a status the planner named (forecast or not), or the CS section.
const TEAM_PSR_SCOPE: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-scope-switch',
  title: 'Scope: Planner set, All SKUs, or CS',
  body:
    'Next to the screen tabs is the scope switch. "Planner set" is the usual view — the planner-set SKUs the ' +
    'engine forecasts. "All SKUs" widens it to every SKU whose status is CS, L, MUL, N, P, CAW, PP or RM ' +
    '(raw materials, planned with their recipe usage), plus the sell-down lines (TBD and AMZ), ' +
    'whether it has a forecast or not: a SKU with no forecast still shows its real stock and any ' +
    'incoming orders, marked "No forecast — plan manually", so nothing goes missing. A count at ' +
    'the top of the page shows how many SKUs were left out because their status is not in that ' +
    'list, so you can see what is excluded. "CS / NPD" is a separate section for CS SKUs, kept apart from the main queue. ' +
    'The scope you pick is kept in the page address, so a link opens the same view.',
};

// SPEC-561 (G85): the sell-down chip on a queue row. The anchor renders only on a sell-down line
// (TBD / AMZ since SPEC-569, All SKUs scope), so the step skips gracefully when none is on screen.
const TEAM_PSR_SELL_DOWN: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-sell-down',
  title: 'Sell-down lines: cover shown, no new buying',
  body:
    'A line marked "sell-down: no new buying" has status TBD (to be discontinued) or AMZ ' +
    '(Amazon). It is forecast while it still sells, so you see its stock, cover, the week it is ' +
    'projected to run out and the weekly projection, but the console never suggests an order for ' +
    'it. A sell-down line with no sale in the 12 weeks before the forecast was made has no forecast, ' +
    'like any other line.',
};

// SPEC-257: the full-catalogue lookup on the PSR console. Honest about the table↔search
// split — the table is the planner-set exception worklist, search reaches any SKU that exists.
const TEAM_PSR_SEARCH: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-search',
  title: 'Look up any SKU, not just the worklist',
  body:
    'The table below is the exception worklist — the planner-set SKUs the engine has ' +
    'flagged, ranked by priority. To open any other SKU that exists — whatever its status ' +
    '(RM, L, MUL, N, TBD) — type a code or name here and pick it. A SKU that carries a ' +
    'filed forecast opens with the same projection, cover and recommendation as a worklist ' +
    'row; a SKU with no forecast opens honestly with its status, supplier and current ' +
    'stock and a note that the engine produces no order for it — nothing is invented. A SKU ' +
    'from outside the worklist opens above the table, marked as a lookup. The ' +
    'status filter chips above the table let you narrow the worklist by product status.',
};

// SPEC-272 / SPEC-292: the plan-by selector on the PSR console. Imported vs Produced is now
// the SCREEN switch (above), so plan-by groups the current screen by supplier, lead-time
// band or buyer instead of a flat SKU list.
const TEAM_PSR_PLAN_BY: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-plan-by',
  title: 'Plan by SKU, supplier, lead time or buyer',
  body:
    'By default the worklist for this screen is a flat table of products, most urgent first. Use ' +
    '"Plan by" to group it instead — by supplier, lead-time band, or buyer. (Imported versus ' +
    'Produced is the screen switch above, so it is not a grouping here.) Each group shows a ' +
    'heading with its SKU count, how many are exceptions, the worst cover in the group, and the ' +
    'total demand and incoming stock over the horizon. The grouping is worked out on the server, ' +
    'so the totals are exact, not a rough on-screen sum. Ordering inside each group stays the ' +
    'same priority order as the flat table, and a row still expands in place.',
};

// SPEC-299: the weekly workings download. Honest (SPEC-145) — it exports exactly the
// projection the detail pane renders, for every SKU on the current screen/scope/filters.
const TEAM_PSR_WORKINGS_EXPORT: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-workings-export',
  title: 'Download the weekly workings',
  body:
    'The "Workings" button downloads the weekly projection as a spreadsheet — one row per SKU ' +
    'per week, with the opening stock, the PO inbound (and the PO numbers behind it), the ' +
    'forecast and its source, the closing stock, the cover, and the week\'s state. It is the ' +
    'same ledger an expanded product row shows, for every SKU on the screen and scope you are viewing, ' +
    'honouring the current filters or grouping — so you can check the maths in Excel. The file ' +
    'opens cleanly with a header row that names the scope, screen and data date. "Export" next ' +
    'to it is the one-row-per-SKU worklist; this one is the week-by-week walk.',
};

/* SPEC-441: the hinge step. The console no longer auto-opens a product, so the five steps
 * below — all anchored INSIDE the detail — have no anchor on a fresh load and skip gracefully.
 * That is honest but silent, and it would cost the walk-through half its content. This step
 * anchors to the table itself (always present) and tells the reader to open a product, so the
 * sections the next steps describe are on screen when they run. */
const TEAM_PSR_OPEN_A_PRODUCT: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-queue',
  title: 'Open a product to work it',
  body:
    'This table is the worklist. Nothing is opened for you — click any row (or its arrow) and it ' +
    'expands in place to show that product\'s weekly projection, its cover, the engine\'s buy ' +
    'recommendation and the reasoning behind it. Click the same row again to close it. Open one ' +
    'now: the next few steps describe what appears inside an open row, and they are skipped while ' +
    'every row is closed. A product you reach by search, or by a link from another screen, opens ' +
    'the same way — above the table when it is not in the worklist, labelled with the reason.',
};

/* SPEC-258: the next-action row on the console detail (Order · Delay · Hold ·
 * Expedite). Honesty rule (SPEC-145): claims only what v1 does — ORDER is fully
 * functional (records a PO or work-order recommendation for manual approval), the
 * other three record the decision for now. No automatic ordering. */
const TEAM_PSR_NEXT_ACTIONS: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-next-actions',
  title: 'Act on a SKU: order, expedite, delay, hold or cancel',
  body:
    'Once you have picked a SKU, these are the next actions. Order opens a panel with the ' +
    'engine\'s quantity in cases and the target arrival week (order-by week plus lead time) — both ' +
    'amendable — and any other SKUs from the same supplier that also have an open gap, so you can ' +
    'consolidate them onto one order. Confirming records a recommendation for a person to approve: ' +
    'a produced item becomes a work-order recommendation, a bought-in item a PO recommendation that ' +
    'lands in PO approval. Expedite, Delay, Hold and Cancel each act on a specific open PO — pick one ' +
    'from the suggested-actions list, or the panel resolves it from the current suggestion — and ' +
    'record your decision and a note (the purchase order change is still made by hand for now). ' +
    'Every amendment you make is logged with ' +
    'who changed what and when, and shows in the PO approval detail — nothing orders automatically. ' +
    'Each action ticks off as recorded, then validates on its own once its effect shows in the ' +
    'Portal — a new purchase order for an ordered SKU, or an ETA that moves after an expedite or ' +
    'delay (a hold or cancel has no ETA move to confirm, so it stays a recorded decision). ' +
    'After you confirm, the panel says what was recorded and offers "See it in Orders raised"; if ' +
    'a record is refused, it shows the reason and that nothing was recorded. If no reply comes ' +
    'back, it says the save may or may not have gone through and links to Orders raised.',
};

/* SPEC-538: where every order raised sits. The button renders only for a login that can raise
 * orders, so the step skips gracefully otherwise (SPEC-145). */
const TEAM_PSR_ORDERS_RAISED: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-orders-raised',
  title: 'Orders raised: every line, one list',
  body:
    'Every order and action you raise is listed here, newest first, line by line — including the ' +
    'lines you added to another product\'s order. Each line shows the supplier, buyer, cases, ' +
    'arrival week, estimated value and where it sits (awaiting PO approval, approved, queried or ' +
    'held). "Download for buyers" saves the same list as a CSV to send on. The same list is on the ' +
    'PO approval tab. These are recommendations for approval — no live order is placed.',
};

/* SPEC-549: the same orders-raised list, shown in full on the PO approval tab (the explainer
 * film "Orders raised" mirrors this step). It renders for every login that can open the tab. */
const TEAM_PO_ORDERS_RAISED: TourStep = {
  route: '/demand-planning?tab=po-approval',
  anchor: 'orders-raised',
  title: 'Orders raised, line by line',
  body:
    'Every line raised from the console, newest first: product, supplier, buyer, cases, arrival ' +
    'week, estimated value, margin on the order, and where it sits. "Download for buyers" saves ' +
    'the same list as a CSV. A line with no recorded cost shows "not known" for its margin, never ' +
    'zero. These are recommendations for approval — no live order is placed.',
};

/* SPEC-549 (mirrors SPEC-543): the margin-at-risk column of the console's product table. */
const TEAM_PSR_MARGIN_AT_RISK: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-margin-at-risk',
  title: 'Margin at risk, beside value short',
  body:
    'Value short is the cases short at the latest buy cost. Margin at risk, beside it, is the ' +
    'cases short times the margin per case: sales value less the landed cost from the order feed. ' +
    'A line with no recorded cost says "not known" rather than showing zero.',
};

// SPEC-554 (G87): reasoning-trail step 6. The anchor sits on the step itself, present whenever an
// open product has a trail; the copy covers both switch positions because the step says which.
const TEAM_PSR_ECONOMIC_STEP: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-economic-step',
  title: 'Economic optimisation: the cost-aware quantity',
  body:
    'Step 6 of the reasoning trail checks whether a different order quantity would cost less a week ' +
    'than the suggestion: delivery and picking per pallet, storage per pallet, the stock-out risk at ' +
    'the margin per case, and the cost of the money tied up. Until the planner rules on it, the step ' +
    'reads "switched off". Once on, it names the cheaper quantity, the weekly saving and what drives ' +
    'it, with the basis of each pound figure under it. A line missing a cost says which one, never a ' +
    'guess. It is advice only: the order quantity is still yours to set.',
};

// SPEC-558 (G88): reasoning-trail step 5. The anchor renders whenever an open product has a trail;
// the copy covers both switch positions because the step says which.
const TEAM_PSR_SHELF_LIFE_STEP: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-shelf-life-step',
  title: 'Shelf-life: what the stock held can still sell',
  body:
    'Step 5 of the reasoning trail checks the batches held against best-before. Until the planner ' +
    'rules on it, the step reads "switched off", states the rule it would apply, and says what ' +
    'data this product holds today. The rule: a batch is not usable when its life left is under ' +
    '90 days or under the customer minimum, whichever is higher. Once on, it shows the short-dated ' +
    'batches with cases and value, the cover the usable stock can still give, and an order cap ' +
    'beside the suggestion. The cap is a flag only: it never changes the suggested quantity. A ' +
    'batch whose life the planner extended shows as an amber tag here in both positions.',
};

// SPEC-593 (G88): the open product's batches and the planner's life extensions. The anchor renders
// once a product is open and its batches could be read; it is absent otherwise, so the step skips.
const TEAM_PSR_BATCH_LIFE: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-batch-life',
  title: 'Batches and life extensions',
  body:
    'The batches this product holds, each judged today: usable, or not usable when its life left ' +
    'is under 90 days or under the customer minimum, whichever is higher. The planning team can ' +
    'extend one batch\'s life to a later date, with a reason. The batch then counts as usable ' +
    'stock to that date. The extension ends on that date, or when it is withdrawn. It can never run longer ' +
    'than the product\'s minimum shelf life. Every extension shows as an amber tag here, on step ' +
    '5 and on the queue row.',
};

// SPEC-558 (G88): the customer minimum life table. The anchor renders once the console has loaded
// and the table could be read; it is absent otherwise, so the step skips gracefully.
const TEAM_PSR_CUSTOMER_MIN_LIFE: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-customer-min-life',
  title: 'Customer minimum life',
  body:
    'The days of life a delivery must still have when it reaches the customer. The order system ' +
    'does not hold this, so it is kept here, for a channel or for one customer. A customer\'s own ' +
    'entry wins over its channel\'s. Open it to see the list; with order rights you can add an ' +
    'entry or remove one. A new entry for the same channel or customer replaces the old one, which ' +
    'is kept for the record.',
};

// SPEC-279: the suggested-actions list. Anchor is absent unless the selected SKU has a timing
// deficit or excess cover with an open PO to act on, so this step SKIPS gracefully (SPEC-145)
// rather than spotlighting an empty pane — the copy claims only what the list shows.
const TEAM_PSR_ACTION_SUGGESTIONS: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-action-suggestions',
  title: 'Suggested actions on existing POs',
  body:
    'When a SKU already has an open purchase order, the system suggests what to do with it and ' +
    'names the PO. If enough is on order but it lands too late, it suggests Expedite — pull that ' +
    'PO forward to the week the SKU runs short. If a PO arrives into weeks that already have plenty ' +
    'of cover, it suggests Delay to the week the stock is actually needed, or Cancel / Hold when the ' +
    'PO is not needed at all in the horizon. Each suggestion shows the PO number, the current arrival ' +
    'week and the suggested one, and what the change would do to cover. Clicking it opens the record ' +
    'panel already filled in with that PO. It is advisory — recording the decision does not move the ' +
    'PO or its date; that is still done by hand. If a SKU has no PO to act on, this list does not appear.',
};

// SPEC-242: the inbound-ETA drill on the PSR console detail. Anchor is absent until a
// selected SKU actually has a past-due open PO or a recorded ETA change, so this step
// SKIPS gracefully (SPEC-145) rather than spotlighting an empty pane — the copy claims
// only what the strip shows when it is present.
const TEAM_PSR_INBOUND_ETA: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-inbound-eta',
  title: 'When an inbound PO slips or is overdue',
  body:
    'When a SKU you have selected has an open purchase order whose arrival date has already ' +
    'passed, an "ETA past due" flag appears here with how many POs and cases are affected — a ' +
    'prompt to chase the supplier or re-date the order. Where the date passed before the portal\'s ' +
    'last update and has not been moved, an "Arrival date passed" pill names that too. ' +
    'Below it, "Recent ETA changes" lists any ' +
    'moves the Portal feed has made to those POs — for example "ETA moved 12 Aug → 26 Aug ' +
    '(+14 days, delayed)" — so a slip is visible instead of quietly changing the projection. It ' +
    'is a record of what happened, not a recommendation: what to do about a slip is still your ' +
    'call. If nothing is overdue and no ETA has moved, this section does not appear.',
};

// SPEC-271: the per-PO supply-override controls. The anchor is absent unless the selected
// SKU actually has an overdue / no-ETA PO being counted as supply, so this step SKIPS
// gracefully (SPEC-145) rather than spotlighting an empty pane.
const TEAM_PSR_SUPPLY_OVERRIDES: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-supply-overrides',
  title: 'Overdue and no-ETA containers now count as supply',
  body:
    'When a purchase order is past its arrival date, or has no arrival date yet, its cases ' +
    'are now counted in this week’s supply — so a product with a container already on the ' +
    'water no longer reads "order now" (your 5 August ask). Each counted PO is listed here. If ' +
    'a container may not land, remove it from the sum in one click — it stays visible, marked ' +
    'excluded — or set the week you assume it arrives. That assumption never changes the PO’s ' +
    'real arrival date on record. If the PO later gains a real arrival date, that date is used ' +
    'and your old assumption is flagged here as superseded so you can clear it. This section ' +
    'appears only when a selected SKU has such a PO.',
};

// SPEC-284 (Workbench §35): the per-SKU audit trail on the console detail. The anchor is
// absent unless the selected SKU actually has a recorded change, so this step SKIPS
// gracefully (SPEC-145) rather than spotlighting an empty pane.
const TEAM_PSR_AUDIT: TourStep = {
  route: '/demand-planning?tab=psr-console',
  anchor: 'psr-audit',
  title: 'The full change history for a SKU',
  body:
    'Every recorded change to a selected SKU is gathered here in one list, newest first: a ' +
    'forecast amendment, an order decision, a purchase-order arrival date that moved, a supply ' +
    'override you set, a manual stock add-back, or a change to the cover-band rules. Each line ' +
    'shows what changed, from what to what, who made the change and when, and the reason where ' +
    'one was given. Where a record cannot say something honestly — no earlier value was kept, or ' +
    'the person’s role was not recorded — it says so plainly rather than guessing. It is a record ' +
    'of what happened, not a recommendation. This section appears only when the selected SKU has ' +
    'a change on record.',
};

const TEAM_PO_APPROVAL: TourStep = {
  route: '/demand-planning?tab=po-approval',
  anchor: 'po-approval',
  title: 'Every buy is signed off by a person',
  body:
    'Every proposed order waits here for a human sign-off before it is placed — the Decision ' +
    'column shows each as approved, queried, held, or still pending. Select a line and its detail ' +
    'opens in the side panel — who raised it, its dates, comments and a note box — or below the ' +
    'table on a narrow screen. The platform never buys on its own: every order is approved by a person ' +
    '(rule G4). Order recommendations you raise from the console appear here too, in the ' +
    '"Order recommendations" panel — approve, query or hold each one with its full amendment ' +
    'history; an approved recommendation is the actionable output for the buyer (nothing is ' +
    'ordered automatically). A product focus narrows the PO lines but never that panel: it lists ' +
    'every recorded action and highlights the ones for the product, including lines added to ' +
    'another product\'s order. Below it, "Orders raised" lists every line with a download for the ' +
    'buyers. The main list is saleable goods; packaging orders sit in their own collapsible ' +
    '"Packaging POs" section below so they do not clutter the review — they are still genuine ' +
    'commitments and still count in the total value. The banner at the top says whether you are ' +
    'looking at the Portal PO Request queue (with its intelligence overlay) or, until that ' +
    'feed is loaded, SuryaIQ\'s proposals from purchase-order history.',
};

const TEAM_STOCK_COVER: TourStep = {
  route: '/demand-planning?tab=stock-cover',
  anchor: 'stock-cover',
  title: 'Where the stock stands',
  body:
    'Every line carries two covers side by side — the planner\'s two demand bases. ROS cover is ' +
    'weeks at the current rate of sale from live orders (the safeguard). Forecast cover walks the ' +
    'stock through the filed weekly forecast, so a heavy week soon shows less cover than a flat ' +
    'average would. The one marked "of record" is the basis that line is planned on; a forecast ' +
    'cover reading "No filed forecast" means no full forecast is filed, so the ROS safeguard governs. ' +
    'Out of stock, below the low mark, healthy, or excess follows the of-record figure. Select a ' +
    'line and its stock breakdown and demand signals open in the side panel; the ⓘ on each ' +
    'headline tile says what it counts.',
};

const TEAM_WAREHOUSE: TourStep = {
  route: '/demand-planning?tab=warehouses',
  anchor: 'warehouses',
  title: 'Stock by site',
  body:
    'The same stock split by warehouse — value held, lines in stock, and what is on QC hold, ' +
    'per site. Only UK sites are shown — stock at non-UK sites is excluded per Sudhin\'s ' +
    'instruction, and the excluded value is listed separately so the totals still reconcile. ' +
    'The MCL stores sit on one MCL row; select it to see each MCL site and what it holds. ' +
    'A site we no longer use is listed only while it still holds stock, and stays flagged. ' +
    'Select any other site for its near-dated batches and its category breakdown, shown in the ' +
    'side panel. Values are ' +
    'the latest purchase cost per case, and the badge names the snapshot — stamping its date where ' +
    'the feed carries one. Picked stock is not totalled here because the source can omit pallets ' +
    'whose only remaining quantity is picked; picked is outside the planning number.',
};

/* SPEC-492 — where an open PO's arrival date comes from. The anchor renders only on a line
 * whose held dates are far enough apart to show a second chip, so the step SKIPS (SPEC-145)
 * when no line has one. Copy names only what the chips show; no number is claimed. */
const TEAM_PO_ETA_SOURCES: TourStep = {
  route: '/supply-chain?tab=pos',
  anchor: 'sc-po-eta-sources',
  title: 'Where each arrival date comes from',
  body:
    'The portal can hold three arrival dates for one PO line: the PO header date, the container ' +
    'date and the promise entered on the PO. A bold chip names the date the cover plans on and ' +
    'which of the three it came from. When the date has passed, no chip is bold and a short note ' +
    'says what the cover does instead. When the dates differ by a couple of days or more, each ' +
    'shows beside it with its own "as of" date — hover a chip to read it. An "Arrival date ' +
    'passed" pill means the date has gone by and the goods have not arrived. Nothing here ' +
    'changes a cover or buy number, and no date is written back to the portal.',
};

/* SPEC-188 — internal transfers view (team tour; internal tiers only). Honesty
 * rule: describes only the read-only table on screen, and skips gracefully if the
 * feed hasn't landed (the anchor is absent until the tab renders). */
const TEAM_TRANSFERS: TourStep = {
  route: '/supply-chain?tab=transfers',
  anchor: 'sc-transfers',
  title: 'Where our own stock is moving',
  body:
    'Site-to-site transfers between our own warehouses — from, to, quantities requested, ' +
    'dispatched and received, and expected arrival. These are internal moves, not supplier ' +
    'orders, so they are shown for visibility only and are never counted as incoming supply.',
};

/* ── SPEC-474: Demand Planning, tab by tab ────────────────────────────────────
 * One step per tab, each naming that tab's FIRST action. Shared steps are reused verbatim
 * (copy lives once); the steps below cover the tabs no tour reached before. Honesty
 * (SPEC-145): each anchor is an always-rendered element of its tab, so a step skips only when
 * the reader's role cannot see the tab. Narration states what is on screen, never a number. */
const DP_DEMAND_BASIS: TourStep = {
  route: '/demand-planning?tab=overview',
  anchor: 'demand-basis',
  title: 'Which forecast this page reads',
  body:
    'Every tab on this page sizes from the same forecast: the as-filed figure, which includes ' +
    'your amendments and the rate-of-sale safeguard. This line says so on every tab, so a ' +
    'number here never looks like a different figure from the demand book disagreeing with it.',
};

const DP_ROW_DETAIL: TourStep = {
  route: '/demand-planning?tab=stock-cover',
  anchor: 'stock-cover',
  title: 'Select a row; its detail opens on the right',
  body:
    'On the stock, shortage, best-before, warehouse, PO, demand-class and planner set tabs you ' +
    'select a row instead of expanding it. Its detail opens in the side panel on the right, ' +
    'next to the table, and stays selected when you come back to the tab. On a narrow screen ' +
    'the same detail opens below the table. Where a figure is missing, a grey label says why — ' +
    'not recorded at the source, not enough history, or waiting on a feed — and its ⓘ explains ' +
    'the label. A missing figure is never shown as zero.',
};

const DP_DEFICIT: TourStep = {
  route: '/demand-planning?tab=deficit',
  anchor: 'deficit',
  title: 'Which products run short, and when',
  body:
    'Each product\'s stock is walked forward week by week: stock now, plus what is due in, minus ' +
    'the forecast. The first week it goes below zero is its first deficit week. Filter to the ' +
    'deficits, then select a product to see its week-by-week position in the side panel.',
};

const DP_AMENDMENTS: TourStep = {
  route: '/demand-planning?tab=amendments',
  anchor: 'dp-amendments',
  title: 'Every amendment, and whether it helped',
  body:
    'All your forecast amendments in one list: what is active, what has lapsed, and whether each ' +
    'one helped or hurt accuracy once the week\'s orders came in. Start with the tiles at the top; ' +
    'a change waiting for its week to arrive is held, and one due to lapse asks you to keep or ' +
    'amend it first.',
};

const DP_SCENARIOS: TourStep = {
  route: '/demand-planning?tab=scenarios',
  anchor: 'dp-scenarios',
  title: 'What if a supplier is late, or demand spikes?',
  body:
    'Each card is one what-if: a supplier delay, a demand spike, or readiness for the coming ' +
    'period. Each one says its assumption in words and gives the result underneath; Show the ' +
    'detail lists the suppliers or categories it would hit. A what-if changes no plan and no order.',
};

// SPEC-525: the assumptions are read-only choices from a short list.
const DP_SCENARIO_ASSUMPTIONS: TourStep = {
  route: '/demand-planning?tab=scenarios',
  anchor: 'scenario-assumptions',
  title: 'Change the assumption',
  body:
    'Pick a different assumption — a 7, 14 or 28-day delay, the top 5 or top 10 suppliers or ' +
    'just one; a 10, 20 or 30 % rise in the top categories or one category; next quarter or the ' +
    'next 13 weeks. The card works it out again. If the figures cannot answer a choice, the card ' +
    'says so instead of showing a number.',
};

const DP_SCENARIO_TO_REQUEST: TourStep = {
  route: '/demand-planning?tab=scenarios',
  anchor: 'scenario-to-request',
  title: 'Turn a what-if into a request',
  body:
    'Turn this into a request opens the feedback panel as a scenario idea, already filled in with ' +
    'the card\'s assumption and what it showed. Add what you would like done, and send it. It ' +
    'becomes a tracked request; it does not change the plan.',
};

// SPEC-521: one intake channel. The suggestion form became the feedback panel pre-set to
// "Scenario idea", and "My requests" is the person's own feedback list with real statuses.
const DP_SCENARIO_SUGGEST: TourStep = {
  route: '/demand-planning?tab=scenarios',
  anchor: 'scenario-suggest',
  title: 'Suggest a new scenario',
  body:
    'Under Your ideas and requests, Suggest a scenario opens the feedback panel set to "Scenario ' +
    'idea": take a picture of what you are looking at and describe the what-if you would like ' +
    'to model.',
};

// SPEC-525: anchored only when the list has something in it, so the step skips for a person
// who has sent nothing yet.
const DP_SCENARIO_MY_REQUESTS: TourStep = {
  route: '/demand-planning?tab=scenarios',
  anchor: 'scenario-my-requests',
  title: 'See what happened to your requests',
  body:
    'My Requests lists everything you have sent, newest first, with where each one stands — ' +
    'received, looking into it, answered, not doing and why, or fixed with the release it ' +
    'shipped in.',
};

const DP_CALCULATIONS: TourStep = {
  route: '/demand-planning?tab=calculations',
  anchor: 'dp-calculations',
  title: 'How a forecast was worked out',
  body:
    'Search for a product or a customer. For a product you see the inputs behind its forecast ' +
    'step by step; for a customer, its products, and you can open any of them the same way.',
};

const DP_PRODUCTION: TourStep = {
  route: '/demand-planning?tab=production',
  anchor: 'dp-production',
  title: 'The factory, in fixed figures',
  body:
    'The packing lines, milling and cleaning capacity at Harwich. These figures are copied from ' +
    'the production planning flowchart, not read live from the factory — the ⓘ on each tile ' +
    'says so — so they change only when the flowchart does.',
};

const DP_PIPELINE_RUNS: TourStep = {
  route: '/demand-planning?tab=pipeline-runs',
  anchor: 'dp-pipeline-runs',
  title: 'When the forecast last ran',
  body:
    'Each forecast run with its start, its length and how many products it covered. Select a ' +
    'run to see which forecasts it moved and by how much.',
};

/* ── Exec Risk Snapshot (SPEC-157; SPEC-515: the Risk cards of the Executive view) ─
 * Honesty rule (SPEC-145): the drill steps target tabs the Executive view can see
 * (stock-cover / deficit / supplier-risk). Step 1 describes what the exec front
 * door leads with without claiming the current viewer's own lens shows it. */
const RISK_SNAPSHOT: TourStep = {
  route: '/',
  anchor: 'overview-lens',
  title: 'The day, in pounds',
  body:
    'Everything starts here. The front door leads with what your view needs first — for the ' +
    'Executive view that is the day\'s risks in money: what is out of stock, what is running short, ' +
    'what is waiting on a decision, and where costs have moved. No jargon, one screen.',
};

const RISK_STOCK: TourStep = {
  route: '/demand-planning?tab=stock-cover',
  title: 'Out of stock, and running low',
  body:
    'The lines with nothing sellable right now, and the ones under eight weeks of cover — the ' +
    'stock walked through the actual forecast weeks ahead, so a heavy week soon surfaces the risk ' +
    'a flat average would hide — each with the sales at risk. Click any tile on the front door to ' +
    'land here, filtered to exactly those lines.',
};

const RISK_GAPS: TourStep = {
  route: '/demand-planning?tab=deficit',
  anchor: 'deficit',
  title: 'What nobody has picked up',
  body:
    'Products the plan says will run short, with no order on the way and no decision recorded ' +
    'against them — the "not looked at yet" list. Where our figure and the order book disagree, ' +
    'it is flagged to look into rather than shown as a certainty.',
};

const RISK_SUPPLIERS: TourStep = {
  route: '/supplier-risk',
  title: 'Suppliers who are late',
  body:
    'Orders placed and past the date the supplier promised — the worst-delayed suppliers first. ' +
    'This is the other half of the traceability trail: it was flagged, the buy was placed, and ' +
    'now the supplier is behind.',
};

/* ── Decisions desk (SPEC-168 — the two-way channel, every audience) ────────
 * Shared across the planner, team and exec landing tours: whoever has a call to
 * make gets the same re-entry point. Written in results language so it is honest
 * for the exec view too. SPEC-462: on desktop the anchor is the left rail's Decisions
 * button, which is always there and carries the count; on mobile it is still the pill,
 * which renders only when a decision is waiting (the step skips there otherwise). */
const DECISIONS_DESK: TourStep = {
  route: '/',
  anchor: 'rulings-pill',
  title: 'Decisions waiting on you',
  body:
    'When there is a call only you can make, this Decisions button shows how many are waiting — each ' +
    'one laid out plainly: who it is for and why, what it is, why we are asking, what runs today, ' +
    'and your options with our recommendation. Each carries a one-week window: answer it, or bring ' +
    'your questions to Param, within the week. If the week passes, Param records a provisional ' +
    'decision so nothing stalls — you will see it marked "decided provisionally" and can still ' +
    'change it any time by answering. Your answer is recorded in your own words, and nothing ' +
    'changes until it is applied and confirmed back to you. If a decision is shared with a ' +
    'colleague and they have already answered, you will see what they said and can agree in one ' +
    'tap or give your own view. If a decision is not yours, choose "Not my decision": we suggest who ' +
    'it belongs to, you can pick anyone and add a note, and it moves to their list (or stays on yours ' +
    'too, if you ask). Param sees every one and updates the record. When nothing is waiting, the ' +
    'button shows no count.',
};

/* ── Supply-chain overview (SPEC-223; SPEC-515: the Supply Chain view) ───────
 * The 3x3 front-door lens of the Supply Chain view. Step 1 stays lens-agnostic
 * (the front door is visible to every role); the drill steps target only tabs the
 * Supply Chain view can see, so they skip gracefully for anyone else. */
const SC_OVERVIEW: TourStep = {
  route: '/',
  anchor: 'overview-lens',
  title: 'Your supply-chain overview',
  body:
    'The front door leads with the top-line supply-chain picture: what is projected to run ' +
    'out ahead — 2, 4, 8 and 12 weeks — how the forecast is scoring, and which promotions are ' +
    'coming. Every number is projected on the current orders plus the forecast, and where a ' +
    'number cannot be counted honestly yet the tile says so instead of guessing.',
};
const SC_FORECAST_QUALITY: TourStep = {
  route: '/demand-planning?tab=forecast&view=accuracy',
  title: 'Forecast quality, line by line',
  body:
    'The forecast page behind the quality tile: which lines are on target, which are off, and ' +
    'the new lines still building up enough history to judge. On the overview it is one headline; ' +
    'here you see it line by line. The tile’s awaiting-checks number counts the flagged lines ' +
    'nobody has checked yet — it falls as you work through them, and refills as checks age out. ' +
    'A line counts as checked while a manual forecast change on it is in force, or if a buying ' +
    'decision was recorded on it since Monday.',
};
const SC_PROMO_CAL: TourStep = {
  // SPEC-364: names `promoView=promotion` deliberately. The by-SKU step that follows sets
  // `promoView=sku`, and a step only pins a param it NAMES — without this the walk-through
  // would step BACK to this one while the by-SKU table was still on screen, and describe
  // the wrong table.
  route: '/demand-planning?tab=promotions&promoView=promotion',
  title: 'Promotions',
  body:
    'Each promotion appears once with its dates, weeks, mechanic and status (confirmed / not ' +
    'confirmed / deleted), plus how many SKUs and customers it covers and its total expected and ' +
    'actual volume. Expand a promotion to see its product-and-customer lines, each with its own ' +
    'volumes and pricing. A blank figure reads "Not sent" (the portal did not provide it) or ' +
    '"After promo" (the actual lands once the promotion ends). The line at the top shows when the ' +
    'feed last updated, so a stale snapshot is never mistaken for fresh.',
};
/* SPEC-360 — the second angle Sudhin asked for on the 14 Aug call: he plans by SKU,
 * account managers plan by customer. The step spotlights the toggle itself, so it skips
 * gracefully if the promotions tab is not in the user's visible set. */
/* SPEC-364: the step now LANDS on the by-SKU view (`?promoView=sku`), because the view is
 * deep-linkable — the toggle writes that into the address bar, so a planner can send the
 * link to someone and they arrive on the same view. The copy is firmed up too: SPEC-360's
 * rows counted only the promotions the page had loaded, so the step had to soften every
 * claim. The by-SKU rows are now built for the SKU, so each row covers every promotion the
 * SKU has in the chosen period, and the note is reserved for a filter that genuinely holds
 * lines back. */
const SC_PROMO_BY_SKU: TourStep = {
  route: '/demand-planning?tab=promotions&promoView=sku',
  anchor: 'promo-view-toggle',
  title: 'Read the same promotions by SKU',
  body:
    'The same promotions, two ways — and this link opens the second one. "By promotion" ' +
    'is the default: one promotion, its customers and products beneath. "By SKU" makes ' +
    'each row one product code — every promotion it is on in the period you chose, how ' +
    'many customers, the earliest start to the latest end, and its expected and actual ' +
    'volume. The row covers the whole SKU, not just the promotions on the page in front ' +
    'of you. Expand a SKU to see each promotion along a period bar, so a product on two ' +
    'customers over overlapping weeks reads as two bars rather than one. Search a product ' +
    'code to go straight to it. A total drawn from only some lines is still marked, a line ' +
    'the portal did not date still has no bar, and a note appears only when a filter you ' +
    'set is holding promotions back from a row. Switching the view updates the address ' +
    'bar, so you can send someone this exact view.',
};
const SC_PLANNING: TourStep = {
  route: '/demand-planning?tab=psr-console',
  title: 'What needs a buy',
  body:
    'The Planning, Scheduling & Replenishment console is where the "POs to raise" tile leads — ' +
    'the lines the plan says will run short with nothing on the way yet. The overview counts ' +
    'them; this is where you act.',
};
/* SPEC-230 — Dan's band pages. Clicking a horizon count opens the full list behind it. */
const SC_OOS_BANDS: TourStep = {
  route: '/oos/12',
  anchor: 'oos-band-page',
  title: 'Open the full out-of-stock list',
  body:
    'Clicking a horizon on the "out of stock ahead" tile opens the whole list behind it — every ' +
    'line projected to run short within 2, 4, 8 or 12 weeks. It is cumulative: a line short soon ' +
    'also shows on the wider pages. And it is a live burn-down — once a line is fixed (an order ' +
    'raised, stock arrived, the forecast amended) it drops off every band at the next refresh. ' +
    'An "All" chip shows every line that fits any of the four horizons, and a sourcing filter ' +
    'narrows the table to produced or imported lines — the counts, the pages and the download all ' +
    'follow the filter, and any line the buying workbook has not typed is named rather than ' +
    'quietly dropped. ' +
    'Open any line to work it in the Planning, Scheduling & Replenishment console, or download the list.',
};

/* ── Planner action board (SPEC-228 — Sudhin's own overview layout) ─────────
 * The front door leads his day with five ranked action worklists. Anchored to the
 * board section, so it skips gracefully (SPEC-145 honesty) for anyone whose lens
 * doesn't render it. Describes only what's on screen — ranked lists, the split, the
 * tick-to-clear — and never claims a source that's still accruing is live. */
const SUDHIN_ACTION_BOARD: TourStep = {
  route: '/',
  anchor: 'sudhin-action-board',
  title: 'Your action worklists',
  body:
    'Your overview leads with the SKUs that need you, ranked: what is critical, the long ' +
    'lead-time lines to order now, where demand is changing, where stock positions have moved, ' +
    'and where pending orders are running well ahead of the forecast. Flip the split to see ' +
    'traded or produced lines on their own. Open any line to work it on the right screen, and ' +
    'tick it once it is actioned — it drops off and the next one takes its place. Each critical ' +
    'line shows the value short and, beside it, the margin at risk (cases short × margin per ' +
    'case); a line with no recorded cost says "margin not known" rather than showing zero. Where a ' +
    'source is still building up, the tile says so rather than showing a number it cannot stand behind.',
};

/* ── Forecast watchlist (SPEC-277 — consistent good/bad + spike) ─────────────
 * Anchored to the watchlist widget, so it skips gracefully (SPEC-145 honesty) on any lens
 * that doesn't render it. Describes only what's on screen — the worst/best lists, the verdict,
 * and the spike flag — and points to the record each row opens. */
const FORECAST_WATCHLIST: TourStep = {
  route: '/',
  anchor: 'forecast-watchlist',
  title: 'Your forecast watchlist',
  body:
    'This lists which of your priority SKUs forecast consistently well or badly — judged on ' +
    'both the backtest and how the forecast has landed against actuals over the same recent weeks as the accuracy check (the tile text names the window). The ' +
    'worst are shown first; flip to the best. A red "spike" flag means a forecast ahead looks ' +
    'implausibly high against the recent average — worth a look before it drives a buy. Open any ' +
    'row to see that SKU\'s forecast-vs-actuals record. A SKU without enough scored weeks is left ' +
    'off rather than forced into good or bad.',
};

/* SPEC-304 — the delivery roadmap step (Mayank's visibility ask). All-roles page, so
 * this is its own short walk rather than a step inside a role-specific tour. Narration
 * states only what the page honestly shows and never a date. */
const ROADMAP: TourStep = {
  route: '/roadmap',
  anchor: 'roadmap-page',
  title: 'Where the work stands',
  body:
    'One page for everyone: what is done and live, what is being built right now, what is ' +
    'next, and what we are waiting on from your side. Only delivered work carries a date — ' +
    'nothing here is a promise of when. It is kept up to date as each piece ships.',
};

/* SPEC-408A — Material Planning Console. The Material Requirements page is now an RM-centric
 * console: one raw material at a time, three views (Usage Forecast / Netting & Supply /
 * Schedule) sharing a header, a lens toggle and a time fence. Both steps sit on the same
 * page and each spotlights an always-rendered anchor (the header, the honesty banner), so
 * neither claims a number that may be absent. */
const MRP_OVERVIEW: TourStep = {
  route: '/material-requirements',
  anchor: 'material-planning-console',
  title: 'One material, three views',
  body:
    'The console works one raw material at a time. Pick a material on the left, then read it ' +
    'three ways: Usage Forecast (how much the plan will draw), Netting & Supply (what is needed ' +
    'against what is coming) and Schedule (when it lands). The Normal, Seasonal and Strategic ' +
    'lens changes how the numbers are weighted. The line under the header names the demand ' +
    'these views size from: the as-filed forecast, including your amendments and the ' +
    'rate-of-sale safeguard.',
};

const MRP_HONESTY: TourStep = {
  route: '/material-requirements',
  anchor: 'material-console-honesty',
  title: 'Honest about the gaps',
  body:
    'The console never shows a number it cannot stand behind. Cover, lead time and shortage ' +
    'timing need raw-material stock and purchase-order feeds we do not hold yet, so each shows ' +
    'an "awaiting feed" note instead of a figure, and the action buttons stay off. Every blocked ' +
    'panel names the exact feed that unblocks it.',
};

/* SPEC-399 — New-Product Matching. A read-only preview: for a product with no history of
 * its own, the closest existing products it can borrow a forecast from, and why each one
 * matched. Both steps sit on always-rendered anchors (the header and the preview banner),
 * so neither spotlights an element that only appears after a search. */
const ANALOGUE_OVERVIEW: TourStep = {
  route: '/analogue-matching',
  anchor: 'analogue-matching',
  title: 'Match a new product',
  body:
    'A new product has no sales history of its own, so we borrow one from the closest ' +
    'existing products. Enter a product code to see its closest matches, how confident the ' +
    'match is, and — the point — why each one matched, from product type to customer.',
};

const ANALOGUE_PREVIEW: TourStep = {
  route: '/analogue-matching',
  anchor: 'analogue-preview-banner',
  title: 'A preview, not a change',
  body:
    'This screen only shows what the matching would suggest. Nothing here is applied to any ' +
    'live forecast or plan — it is a preview, so you can see and sense-check the matches ' +
    'before the matching is switched on for real. One exception: a match you choose yourself ' +
    'is recorded against your name straight away, and takes effect when the matching goes live.',
};

/* SPEC-446 — choosing a different match. This anchor sits inside the result, so it only
 * mounts once a product has been opened; the tour skips the step gracefully until then
 * rather than spotlighting an empty screen (SPEC-145 honesty rule). */
const ANALOGUE_CHOOSE_MATCH: TourStep = {
  route: '/analogue-matching',
  anchor: 'analogue-choose-match',
  title: 'Choose a different match',
  body:
    'Disagree with the match? Search for the product you would use instead, and the forecast ' +
    'on screen is worked out from that one. Your choice is recorded with your name, the date ' +
    'and the match it replaced, and it is the one used when the matching goes live. Clear it ' +
    'at any time — the system match stays on screen beside yours so you can always compare ' +
    'the two.',
};

/* SPEC-403 — Production Upload. Production uploads its weekly build plan and sees the
 * validation result. Both anchors are always on screen (the header and the latest-plan
 * section), so neither step spotlights an element that only appears after an upload. */
const PRODUCTION_UPLOAD_OVERVIEW: TourStep = {
  route: '/production-upload',
  anchor: 'production-upload',
  title: 'Upload the weekly plan',
  body:
    'Production uploads the weekly build plan here — the out-of-ordinary builds the forecast ' +
    'cannot see. Not sure of the exact columns? "Download sample template" just below gives a ' +
    'ready-made example. Drop the workbook in and every row is checked: you see straight away ' +
    'how many rows were accepted and, for any that were not, the reason for each one.',
};

const PRODUCTION_UPLOAD_LATEST: TourStep = {
  route: '/production-upload',
  anchor: 'production-upload-latest',
  title: 'The plan on file',
  body:
    'The most recent plan on file, with its version and who uploaded it. Uploading a new ' +
    'version replaces the plan for that version, so this always shows what production last ' +
    'committed to.',
};

/* SPEC-406 — Stock Targets. A read-only cover-band view: for every product, is its stock
 * cover in band, below the band (buy) or above it (overstock)? Both steps sit on always-
 * rendered anchors (the header and the band-summary card), so neither spotlights an element
 * that only appears once cover has been worked out. */
const STOCK_TARGETS_OVERVIEW: TourStep = {
  route: '/inventory-targets',
  anchor: 'inventory-targets',
  title: 'Is stock on target?',
  body:
    'Cover is how many weeks of demand today’s stock covers. Every product has a target ' +
    'cover band. This page asks one question for each product: is its cover in band, below the ' +
    'band (so buy), or above it (overstock)?',
};

const STOCK_TARGETS_BANDS: TourStep = {
  route: '/inventory-targets',
  anchor: 'stock-targets-bands',
  title: 'In band, below, above',
  body:
    'The summary counts how many products sit in band, below (buy) or above (overstock), with ' +
    'the share in band. The table below breaks it down by demand pattern and then by product, ' +
    'with each product’s current cover, its target band and the suggested move.',
};

/* SPEC-407 — Forecast Benchmark. A read-only trust view: our forecast next to the ERP's own
 * buy report, product by product. Both steps sit on always-rendered anchors (the header and
 * the headline card), so neither spotlights an element that only appears once the comparison
 * has data. */
const FORECAST_BENCHMARK_OVERVIEW: TourStep = {
  route: '/forecast-benchmark',
  anchor: 'forecast-benchmark',
  title: 'Our forecast vs the purchasing system',
  body:
    'The purchasing system decides how much to buy from a simple run-rate. We decide from the ' +
    'forecast. This page puts the two side by side for each product, for the same weeks — ' +
    'this week’s demand, the 13 weeks from this week, cover and what the purchasing system ' +
    'says is still needed — so you can see where they agree and where they part. Ours is the ' +
    'forecast as filed, the same one the plan uses.',
};

const FORECAST_BENCHMARK_HEADLINE: TourStep = {
  route: '/forecast-benchmark',
  anchor: 'forecast-benchmark-headline',
  title: 'Where we agree, at a glance',
  body:
    'The headline says how many products are in the purchasing system’s buy report, how many ' +
    'of them we forecast, and how often our 13-week demand lands within 15% of its own. The ' +
    'ⓘ beside each figure says how it is counted. The table below ' +
    'carries each product with both sets of numbers, the model we used and how accurate that ' +
    'forecast has been — searchable and downloadable.',
};

/* SPEC-434 — V7 promotional engine read-layer pages. A DARK preview: the pages READ the
 * real /api/promo/* numbers; the engine writes nothing to the live plan. Every step claims
 * only what is on screen and skips gracefully when the data or anchor is absent. */
const V7_BOX_A: TourStep = {
  route: '/box-a',
  anchor: 'box-a',
  title: 'The plan of record, product by product',
  body:
    'The published forward demand, line by line. Before you choose a product, the page lists ' +
    'every line in the book; choose one to read it by customer. The top section is the plan the ' +
    'business runs on today; the section beneath it is what the promotional engine proposes, ' +
    'which changes nothing in the published plan.',
};

// SPEC-532: the full list. Shown only before a product is chosen; skips otherwise.
const V7_BOX_A_LINE_SUMMARY: TourStep = {
  route: '/box-a',
  anchor: 'box-a-line-summary',
  title: 'Every line in the published book',
  body:
    'Every line in the book, week by week, with each week added up across customers. The line ' +
    'above the table states how many lines, customers and weeks the book holds — nothing is cut ' +
    'off. "Open by customer" shows one line split by customer. One download saves the lines ' +
    'shown; the other saves the whole book by customer, every week.',
};

const V7_BOX_A_CONTEXT: TourStep = {
  route: '/box-a',
  anchor: 'box-a-context',
  title: 'Choose what you are looking at',
  body:
    'Every figure on this page is for the product, customer and week chosen here. Pick a ' +
    'product on its own to see it across its customers, or add a customer and a week to read ' +
    'a single line. Nothing is shown as a business-wide total. The last line names the demand ' +
    'this page reads: the pre-amendment baseline, before your amendments and the rate-of-sale ' +
    'safeguard — material planning reads the as-filed forecast, which includes both.',
};

const V7_BOX_A_HERO: TourStep = {
  route: '/box-a',
  anchor: 'box-a-hero',
  title: 'The published numbers for your selection',
  body:
    'The base forecast, the promotional signal, any other approved signals and the final ' +
    'number — straight from the published book for the selection above. Where the book holds ' +
    'no value, it says so rather than showing a zero. Every figure on this page is read from ' +
    'ONE published book, named under Book status, so the parts always add up to the final ' +
    'number. When a newer book is published while you are reading, the page tells you and ' +
    'waits for you to load it rather than swapping figures underneath you.',
};

const V7_BOX_A_ALLOCATION: TourStep = {
  route: '/box-a',
  anchor: 'box-a-allocation-caveat',
  title: 'How a customer-level base is worked out',
  body:
    'A base forecast for one customer is the product forecast split across the customers ' +
    'who buy it. It is not a separate forecast built for that customer. The note says which ' +
    'split rule the published plan used, and the ⓘ beside it explains that rule.',
};

/* SPEC-498 (G60) — the share-basis column exists only on a plan published under the grouped
 * split, so this step skips itself on a plan that uses the 26-week split. */
const V7_BOX_A_SHARE_BASIS: TourStep = {
  route: '/box-a',
  anchor: 'box-a-share-basis',
  title: 'Why this line gets this share',
  body:
    'Each line says what its share is based on: the same weeks last year plus its recent ' +
    'trend, the same weeks last year alone, its recent trend alone, or a new listing at its ' +
    'recent rate. A channel line, such as Food Service, pools many customers — hover over ' +
    'its name to see how many.',
};

const V7_BOX_A_CONTRIBUTIONS: TourStep = {
  route: '/box-a',
  anchor: 'box-a-contributions',
  title: 'What makes up the published final number',
  body:
    'The published plan records each part of the demand separately — the base, any ' +
    'promotional lift, distribution, halo, a planner amendment where someone changed the ' +
    'week’s total, and the reductions for cannibalisation and forward buying. Reductions are ' +
    'already negative, so the lines add up to the final number. A part the plan does not ' +
    'record is left out rather than shown as zero.',
};

/* SPEC-454 — the amendment panel is a real write path now, so the walk-through has to teach
 * the two things a planner can get wrong: the box takes the WHOLE week's total (not the
 * change), and the published figures above only move at the next publication. */
const V7_BOX_A_AMENDMENT: TourStep = {
  route: '/box-a',
  anchor: 'box-a-amendment',
  title: 'Changing a week’s total',
  body:
    'Pick one product and one week, then enter the total you want for that week — the whole ' +
    'number, not the change. It is shared across that week’s customers in the same ' +
    'proportions as the forecast. The panel shows the range one change may move, and you can ' +
    'record the agreement behind the number: what commercial said, what the system said, and ' +
    'who agreed it. Your change governs the plan straight away; the published figures ' +
    'show it from the next publication, because a published plan is never edited in place. ' +
    'On a wide screen the form sits in the panel on the right; otherwise it sits below the ' +
    'figures.',
};

const V7_BOX_A_LINES: TourStep = {
  route: '/box-a',
  anchor: 'box-a-lines',
  title: 'Every line keeps its own answer',
  body:
    'When a selection covers several promotion lines, each line carries its own strongest ' +
    'evidence, its own confidence and its own balance proof. They are never averaged into ' +
    'one verdict — a method that wins for one customer need not win for another.',
};

const V7_BOX_A_STRIP: TourStep = {
  route: '/box-a',
  anchor: 'box-a-portfolio-strip',
  title: 'The whole-book total, for context only',
  body:
    'The roll-up across every promotion with enough evidence to carry a signal. It is here ' +
    'for context, not as the answer: totals can match while individual lines run over and ' +
    'under, which is why this page reads by product.',
};

/* SPEC-499 (G71): the demand-of-record basis tile on the landing state. It claims only what the
 * tile shows: the three counts, the preview/live wording, and the list behind them. */
const V7_BOX_A_DEMAND_BASIS: TourStep = {
  route: '/box-a',
  anchor: 'box-a-demand-basis',
  title: 'Where each product’s demand comes from',
  body:
    'Four counts: products fully on the agreed plan, products on the plan with fallback weeks, ' +
    'products fully on a rate-of-sale fallback, and products with no demand signal. A week ' +
    'whose forecast was rejected carries the product’s recent rate of sale — not an agreed-plan ' +
    'number — and every screen labels it. With no rate of sale either, no order is suggested ' +
    'and the product waits for a planner. The tile says whether this is live or a preview of ' +
    'switch-on; open the list to see each product.',
};

const V7_BOX_A_FRESHNESS: TourStep = {
  route: '/box-a',
  anchor: 'promo-freshness',
  title: 'When these numbers were worked out',
  body:
    'The engine runs on a schedule, not while you wait, so the page says when its last run ' +
    'finished and when the promotions behind it were read. If a new run is under way, it ' +
    'says that too — and you keep seeing the previous run until the new one finishes.',
};

// SPEC-541 — Recommendations and outcomes (Trust) + the exec "Suggested vs actioned" tile. Steps
// claim only what is on screen; before the first nightly run the cards say nothing is recorded yet,
// and the exec step skips on a lens without the tile.
const REC_TREND: TourStep = {
  route: '/recommendations',
  anchor: 'recommendations-trend',
  title: 'What was suggested, and what was done',
  body:
    'Every night the platform records each order, expedite, delay, hold or cancel it suggests. ' +
    'The bars show, week by week, how many were acted on (an order recorded, a purchase order ' +
    'raised or an arrival date moved), how many were not, and how many are still awaiting action.',
};

const REC_LINES: TourStep = {
  route: '/recommendations',
  anchor: 'recommendations-lines',
  title: 'Every suggestion, and what happened',
  body:
    'One row per suggestion: what it was and why, what the team did, and what happened once its ' +
    'window passed — right, wrong, mixed, or cannot be measured. Until the window passes it ' +
    'reads "Not yet known". Beside it, the service level and the lost sales on the customer ' +
    'order book over the same window; they read "Not yet measurable" until the window\'s weeks ' +
    'have finished picking. Filter by week, kind, action or outcome, or download the CSV.',
};

const REC_PROPOSALS: TourStep = {
  route: '/recommendations',
  anchor: 'recommendations-proposals',
  title: 'What the record suggests changing',
  body:
    'When it is switched on, this lists settings the judged suggestions point to — a safety ' +
    'cushion, a reorder point or a lead time — each with its evidence. Nothing changes by ' +
    'itself: a planner applies each one. While it is off, the card says so.',
};

const REC_EXEC_TILE: TourStep = {
  route: '/',
  anchor: 'overview-suggested-vs-actioned',
  title: 'Suggested vs actioned',
  body:
    'How many of the platform’s suggestions in the last 8 weeks were acted on, and how many ' +
    'lines were out of stock at the start and end of those weeks, by category. Open it to see ' +
    'each suggestion and how it turned out.',
};

// SPEC-549 (mirrors SPEC-543): the "Margin this month" tile on the Executive view. Before order
// cost is on record the tile says so, and shows no figure.
const EXEC_MARGIN_MONTH: TourStep = {
  route: '/',
  anchor: 'overview-margin-month',
  title: 'Margin this month',
  body:
    'Sales value less the landed cost per case on this month\'s orders (margin after landed ' +
    'cost), with the share of order lines that carry a cost in small print. Below it, the ' +
    'delivered margin, after promotion rebates and warehouse costs, where those are known. Until ' +
    'order cost is on record, the tile says so and shows no figure. Open it for the margin view in Reports.',
};

// SPEC-455 — the demand-source comparison, the watching period before "the agreed plan
// everywhere". Steps claim only what is on screen: the live source, the day-by-day agreement,
// and the reasons for the differences. Each skips gracefully before any day is compared.
const DEMAND_SOURCE_STATE: TourStep = {
  route: '/demand-source',
  anchor: 'demand-source-state',
  title: 'Which demand number is live',
  body:
    'Every demand number on the platform — cover, the shortfall, the schedule, what to buy — ' +
    'comes from one place. This says which place that is today. If the agreed plan was ' +
    'selected but is too old to use, this panel says so and tells you the platform has gone ' +
    'back to the filed forecast for every product.',
};

const DEMAND_SOURCE_FRESHNESS: TourStep = {
  route: '/demand-source',
  anchor: 'demand-source-freshness',
  title: 'How fresh the filed forecast is',
  body:
    'The line under the title gives the date of the filed forecast, one of the two numbers ' +
    'compared here. The agreed plan’s own publish date, and the day compared, sit in the ' +
    'latest-comparison card further down.',
};

const DEMAND_SOURCE_TREND: TourStep = {
  route: '/demand-source',
  anchor: 'demand-source-trend',
  title: 'Watching them agree',
  body:
    'Each night both numbers are worked out and compared, product by product and week by ' +
    'week. This line is how much they matched, day after day. A run of days at or near 100% ' +
    'is what earns the change — nothing moves onto the agreed plan until you are satisfied.',
};

const DEMAND_SOURCE_CAUSES: TourStep = {
  route: '/demand-source',
  anchor: 'demand-source-causes',
  title: 'Why they differ',
  body:
    'Every difference gets a reason. An amendment you filed after the plan was last agreed ' +
    'closes up at the next nightly refresh. A product-week the agreed plan does not carry ' +
    'keeps the rate-of-sale safeguard, exactly as today. Anything left over is marked as not ' +
    'yet explained — those are the rows worth a look before the change.',
};

const V7_BOX_A_DECOMPOSITION: TourStep = {
  route: '/box-a',
  anchor: 'box-a-decomposition',
  title: 'How the preview builds its final number',
  body:
    'For the selected line: base plus the proposed promotional signal equals the preview ' +
    'final, proved to balance. The smaller building blocks — distribution, halo, ' +
    'cannibalisation, forward buying — are labelled "insufficient data" until their source ' +
    'arrives, never guessed at.',
};

const V7_BOX_A_CHAIN: TourStep = {
  route: '/box-a',
  anchor: 'box-a-chain',
  title: 'System, amendment, approval',
  body:
    'This chain is about the PROMOTIONAL forecast, which the plan does not carry yet, so the ' +
    'amendment and approval stages are shown read-only here. Changing the plan’s own weekly ' +
    'total is a different thing, and it is live in the panel further up this page.',
};

const V7_WORKBENCH: TourStep = {
  route: '/forecast-workbench',
  anchor: 'forecast-workbench',
  title: 'The working forecast view',
  body:
    // Results language only: this block sits inside the exec-copy region the SPEC-157 language
    // guard scans, so the planner's own terms for the product-grouping columns stay on the
    // screen (where they are his words) and out of the walk-through copy.
    'Every customer and product in the published forecast book, one row per week: the base ' +
    'forecast, the promotional lift on top where a promotion exists, and the final number, ' +
    'with the product group and how steady its demand has been beside it. Type a product code, ' +
    'a product name or a customer in the search box, or narrow it with the pickers — the whole ' +
    'book is searched, and the table pages through the matches with the count beside the title. ' +
    'Most lines have no promotion; those say "no promotion" rather than showing a blank. A ' +
    'product the book does not hold tells you why. "Download CSV" saves exactly what the ' +
    'filters show. Four tabs switch the view: the weekly numbers, how each line’s number is ' +
    'built, a comparison against a simple reference forecast, and where each number came from ' +
    '— the last three summarise a whole line rather than a single week. The line under the ' +
    'headline says when the engine last worked the promotional numbers out.',
};

/* SPEC-457 — the thumbs beside the Final figure.
 *
 * Anchored on the CONTROL, not the table: the control renders nothing when the switch is
 * off, so this step skips rather than narrating something that is not on screen (SPEC-145
 * honesty rule). The copy states exactly what a press does and, as importantly, what it does
 * NOT do — the whole point of the instrument is that it carries no implied promise. */
const V7_WORKBENCH_FEEDBACK: TourStep = {
  route: '/forecast-workbench',
  anchor: 'forecast-feedback-thumbs',
  title: 'Flagging a number that looks wrong',
  body:
    'Beside each final number there is a thumbs-down. Press it to flag a number that does not ' +
    'look right to you. There is no thumbs-up: a good number needs nothing from you. Here is ' +
    'exactly what happens: your flag is written down for the weekly review, and nothing else. ' +
    'It does not change the number, it does not re-run anything, and nobody is paged. A flag ' +
    'offers one optional line for what is wrong, and a "Request a change?" link if you want ' +
    'something actually done. That link is the only route to action, and you choose it ' +
    'deliberately. Your flag is private until the review, so nobody else sees what you pressed. ' +
    'You are asked again only when the number itself is next re-worked. The thumbs-down ' +
    'appears only where there is a real number to judge.',
};

const V7_WORKBENCH_QUALITY: TourStep = {
  route: '/forecast-workbench',
  anchor: 'forecast-workbench-quality',
  title: 'Accuracy waits for actuals',
  body:
    'Accuracy, over- or under-forecast and forecast value-add need the promotion’s real sales ' +
    'to compare against. The promotion has not run yet, so these read "awaiting actuals" — an ' +
    'honest blank, not a made-up score.',
};

/* SPEC-468 — the "Model accuracy (back-test)" headline tile. Anchored on the tile itself, so
 * the step skips if the tile is absent. Results language only: this block sits inside the
 * exec-copy region the language guard scans (no methodology terms). */
const V7_WORKBENCH_BACKTEST: TourStep = {
  route: '/forecast-workbench',
  anchor: 'forecast-workbench-backtest',
  title: 'How well the model fitted the past',
  body:
    'This headline figure is available today. Before a forecasting model is chosen for a ' +
    'product, its latest weeks of sales are hidden from it, and the model is scored on how ' +
    'closely it predicted them. The large number is that error; the line under it says ' +
    'whether the model ran high or low. With one product selected you see its own score and ' +
    'the model that won. With several, bigger sellers count for more, and the rule is written ' +
    'under the headline row. A product that was never tested says why instead of showing a ' +
    'score. This is NOT how the published forecast is doing against real orders — the two ' +
    'figures beside it measure that, and they wait for the weeks to pass.',
};

/* SPEC-485 — the "Why did recent weeks miss?" panel. Anchored on the panel card, so the step
 * skips when the page has not loaded it. Results language: this block sits inside the
 * exec-copy region the language guard scans (no methodology terms). */
const V7_WORKBENCH_MISSES: TourStep = {
  route: '/forecast-workbench',
  anchor: 'forecast-workbench-misses',
  title: 'Why recent weeks missed',
  body:
    'When a finished week landed outside its accuracy line, this panel names one reason, ' +
    'looked up from what the platform recorded that week: the order sync failing, a manual ' +
    'change, the rate-of-sale safeguard, a promotion, a product status change or a change of ' +
    'forecasting method. The first that applies in a published order is shown, the others ' +
    'underneath, and the record behind each one opens on request. When nothing was recorded ' +
    'it says so plainly rather than guessing. One product selected shows each week; more ' +
    'than one shows how many misses fall under each reason. Weeks that cannot be scored yet ' +
    'are named, never counted as a miss.',
};

/* SPEC-577 — the page opens By SKU, so this step lands there. */
const V7_PROMOTIONS: TourStep = {
  route: '/promotions?view=sku',
  anchor: 'promotions',
  title: 'The promotional portfolio',
  body:
    'Every confirmed promotion with its baseline forecast, uplift and final number. The page ' +
    'opens By SKU, one row per product line. Read the same promotions four ways with the ' +
    'toggle — by SKU, by retailer event, by mechanic or by category — and download whichever ' +
    'view you are looking at. A fifth, "By trading event", ' +
    'lines the same event up across years. Each headline tile says what it sums: the baseline ' +
    'covers every line with a base, split by where the base came from, and the promotional ' +
    'signal only the promotions with enough evidence behind them. Under each base the list ' +
    'says its source — the published plan, or the customer\'s own ordinary orders where the ' +
    'plan has no figure. The line under the headline says when the engine last worked these ' +
    'numbers out.',
};

/* SPEC-572 — the strip that says what the engine did in this run. Every item is placed by what
 * the run recorded, so the step names the two lists, not their contents. The strip renders only
 * once a run exists, so with no run the step skips. */
const V7_PROMOTIONS_ENGINE: TourStep = {
  route: '/promotions?view=event',
  anchor: 'promotions-engine',
  title: 'What the engine did',
  body:
    'This strip says when the engine last ran and for how many promotions. "In play" lists ' +
    'what it used in this run, such as each line\'s own promotion history. "Waiting on data" ' +
    'lists what it could not use yet, such as an account manager the portal does not send. A ' +
    'method or step that ran but changed no line is listed on its own. Hover an item to see ' +
    'on how many lines it was used.',
};

/* SPEC-580 — the run's governance totals, on the strip. The row renders only when the run
 * recorded tags, so with an older run the step skips. */
const V7_PROMOTIONS_GOVERNANCE: TourStep = {
  route: '/promotions?view=sku',
  anchor: 'promotions-governance',
  title: 'Where a rule changed a figure',
  body:
    'Each line carries tags for the rules that fired on it: a red breach where a rule changed the ' +
    'figure, an amber guard where a rule stopped one, and the Rules column and filters find them.',
};

/* SPEC-572 — the filters Param asked for. Sits on the filter bar inside the list card. */
const V7_PROMOTIONS_FILTERS: TourStep = {
  route: '/promotions?view=event',
  anchor: 'promotions-filters',
  title: 'Narrow the list',
  body:
    'Filter by account manager, mechanic, status, the weeks a promotion runs, how many SKUs ' +
    'it has, its base, uplift or final figure, its confidence and whether it reconciles. ' +
    '"Uplift below zero" finds the promotions that read below their base in one click. The ' +
    'filters stay in the address bar, so you can send the view. They narrow the list and the ' +
    'download, which says when it is filtered; the headline figures stay the whole run. ' +
    'Promotions that have finished are left out at first: the line above the filters says ' +
    'how many, and one click shows them, each marked "Finished".',
};

/* SPEC-443 (audit M1/M2) — two cards a reader must not conflate: how much evidence each
 * promotion gave the engine (readiness) versus where its version stands (lifecycle). The old
 * title put evidence states under a lifecycle word. The step sits on the lifecycle card, so it
 * skips gracefully if the page is still loading. */
const V7_PROMOTIONS_READINESS: TourStep = {
  route: '/promotions?view=event',
  anchor: 'promotions-lifecycle',
  title: 'Readiness and version are two different things',
  body:
    '"Forecast readiness" counts how much evidence each promotion gave the engine to work ' +
    'with. "Version lifecycle" counts where each promotion\'s version stands — draft, ' +
    'released or superseded — and how many are still draft. Draft means recorded, not ' +
    'approved. Approval attaches to a version, and a material change makes a new unapproved ' +
    'version. Nothing promotional reaches the demand book until a version is approved and the ' +
    'switch is on. There is no approve button on screen yet.',
};

/* SPEC-519 (G67) — the by-trading-event view. The anchor renders only when a year column holds
 * a promotion, so with no event data the step skips instead of pointing at an empty grid.
 * The other /promotions steps name `view=event` so the engine navigates between the two. */
const V7_PROMOTIONS_BY_EVENT: TourStep = {
  route: '/promotions?view=trading',
  anchor: 'promo-by-event',
  title: 'Compare a promotion with earlier years',
  body:
    'Each promotion keeps its own ID every time it runs. To compare years, pick a trading ' +
    'event — Ramadan, Eid, Diwali, Christmas, Easter or Chinese New Year — and its promotional ' +
    'period is lined up year by year, with each promotion\'s mechanic, promo price, discount ' +
    'and uplift. A promotion is matched by its dates, then by its name; the ones that match ' +
    'nothing are listed below for you to assign. The promotional calendar under the years ' +
    'shows each period\'s weeks, and planners can change them.',
};

const V7_PROMOTIONS_LIST: TourStep = {
  route: '/promotions?view=event',
  anchor: 'promotions-list',
  title: 'Open any promotion in full',
  body:
    'Expand a row for its SKU lines, or open the detail for the full picture — the governance ' +
    'steps, confidence and traceability for that promotion, and a table with every method\'s ' +
    'numbers for one line: its state, lift, cases, how many past readings it used, its score ' +
    'and why it was set aside. The winner is marked, and a number the engine did not record ' +
    'says "not recorded". The chart, the winner and the table show the line you opened. ' +
    'A line with no base shows "Needs a base" in place of a confidence. Where the run recorded ' +
    'them, a line\'s base names how far back it read the customer\'s own orders, with the ' +
    'published book figure beside it and any label, such as "New line". ' +
    'Hover a draft status to see what draft means. The ' +
    'uplift carries its sign. An uplift below zero shows its reason, and clicking the reason ' +
    'opens the detail. Click a column heading to sort the list, figures largest first; a row ' +
    'with no figure stays at the bottom, and the download keeps the same order.',
};

/* SPEC-585 (G90) — each retailer's order lead. The section renders once the leads are read (a
 * reader without the promotions read tier sees nothing), so otherwise the step skips. */
const V7_PROMOTIONS_ORDER_LEADS: TourStep = {
  route: '/promotions?view=sku',
  anchor: 'promotions-order-leads',
  title: 'When each retailer orders',
  body:
    'Retailers such as Sainsbury\'s order one to three weeks before a promotion starts. The engine ' +
    'learns each retailer\'s order lead from its finished promotions with marked orders, and shows ' +
    'how many it needs. The lead is used only for past promotions with no marked orders: their weeks ' +
    'are read that much earlier. The Order lead column in By SKU shows the lead on every line, or ' +
    '"Not learned" with how many promotions it had. A planner can set a retailer\'s lead here; it ' +
    'replaces the learned one from the next engine run, and the line says "set by planner".',
};

/* SPEC-587 — the rep's expected volume beside the engine, on finished promotions. */
const V7_PROMOTIONS_REP_BENCHMARK: TourStep = {
  route: '/promotions?view=sku',
  anchor: 'promotions-rep-benchmark',
  title: 'Rep vs engine',
  body:
    'Each promotion line carries the sales rep\'s expected quantity. It is the cases for the whole ' +
    'promotion, not per week, and it is a guide. On finished promotions it is set beside the ' +
    'engine\'s forecast made as of the promotion\'s start, and both are checked against what the ' +
    'promotion sold. A line is scored only where the rep\'s figure lands within half to twice the ' +
    'actual; the rest are listed with the reason. Other ways of reading the figure are kept under ' +
    '"Other readings (not used)". Nothing here changes a promotion figure.',
};

/* SPEC-592 — lines whose orders jumped far above their usual level with nothing on the tracker. */
const V7_PROMOTIONS_UNTRACKED: TourStep = {
  route: '/promotions?view=sku',
  anchor: 'promotions-untracked',
  title: 'Untracked promotions',
  body:
    'Each week every customer line is set beside its own usual level (its ordinary orders before ' +
    'the weeks checked). A line whose orders run at 2.5 times that level for 3 weeks, or whose ' +
    'orders already placed for next week reach 4 times it, with nothing on the tracker in or near ' +
    'those weeks, is listed here. Open one to see the weeks and answer it: a promotion (ask the rep ' +
    'to add it), a one-off, or a new listing. It changes no forecast. The thresholds are on ' +
    'Sudhin\'s desk (G100).',
};

/* SPEC-411 (scaffold) — three new pages, each a stub today: the header is live, the rest of
 * the page is being built out in a follow-up. Each step sits on the header anchor (always
 * rendered) and states only what is true right now — never claims a table or chart that
 * isn't on screen yet. */
const REPLENISHMENT_DETAIL_OVERVIEW: TourStep = {
  route: '/replenishment-detail',
  anchor: 'replenishment-detail',
  title: 'The reorder decision, in full',
  body:
    'One product at a time: how much to order, the inputs behind it and the reasoning. The ' +
    'line under the header names the demand, lead time and stock it is worked out on.',
};

const REGIME_ALERTS_OVERVIEW: TourStep = {
  route: '/regime-alerts',
  anchor: 'regime-alerts',
  title: 'When a product’s pattern shifts',
  body:
    'Names the products whose demand pattern has changed, with the history behind each one. ' +
    'The line under the title says how current the demand data behind it is.',
};

const REGIME_ALERTS_SUMMARY: TourStep = {
  route: '/regime-alerts',
  anchor: 'regime-summary',
  title: 'How many products are in each state',
  body:
    'Each product sits in one state: steady, running a promotion, on decline watch, ramping as a ' +
    'new product, or in transition. The tiles count them. The ⓘ on a tile explains the states. ' +
    'If the summary cannot be read, this card says so instead of showing zeros.',
};

const REGIME_ALERTS_LIST: TourStep = {
  route: '/regime-alerts',
  anchor: 'regime-list',
  title: 'Why a product is in its state',
  body:
    'Select a product to open its reason, the evidence behind it, and every change of state ' +
    'since tracking began. A field the record leaves blank shows a “Not recorded” label.',
};

const CUSTOMER_DORMANCY_OVERVIEW: TourStep = {
  route: '/customer-dormancy',
  anchor: 'customer-dormancy',
  title: 'Customers who have gone quiet',
  body:
    'A stand-alone view of customers who have stopped ordering — last order date, the value ' +
    'at risk, and who is worth a call back. The window buttons set how long “quiet” means.',
};

const CUSTOMER_DORMANCY_SUMMARY: TourStep = {
  route: '/customer-dormancy',
  anchor: 'dormancy-summary',
  title: 'The three totals',
  body:
    'Customers gone quiet, the order value tied to them, and how many are worth a call. They ' +
    'total the rows in the table below, with the window and value tier you chose. The ⓘ on a ' +
    'tile says how it is counted.',
};

const CUSTOMER_DORMANCY_LIST: TourStep = {
  route: '/customer-dormancy',
  anchor: 'dormancy-list',
  title: 'Who to ring first',
  body:
    'Sorted by customer value, so the quiet accounts that matter most are at the top. A cell ' +
    'the record leaves blank shows a “Not recorded” label rather than a dash. The list can be ' +
    'downloaded.',
};

const RETRO_BILLING_OVERVIEW: TourStep = {
  route: '/retro-billing',
  anchor: 'retro-billing',
  title: 'Money that may be recoverable',
  body:
    'Two scans run on live data: credit-note leakage and supplier cost rises we absorbed. The ' +
    'ribbon under the title says how fresh that data is. The retailer deduction check is not ' +
    'built yet and is shown as awaiting its feeds.',
};

const RETRO_BILLING_SUMMARY: TourStep = {
  route: '/retro-billing',
  anchor: 'retro-summary',
  title: 'What is open, recoverable and recovered',
  body:
    'Open findings, the £ at stake, the £ already recovered and the critical count. The £ at ' +
    'stake counts only findings that can be valued in £, and says how many cannot. If the scans ' +
    'have not run, the tiles say “Awaiting source” instead of showing zero.',
};

const RETRO_BILLING_FINDINGS: TourStep = {
  route: '/retro-billing',
  anchor: 'retro-findings',
  title: 'Work a finding',
  body:
    'Select a finding to see the invoice or credit-note lines behind it and to record where ' +
    'you are with it: investigating, disputed, recovered or dismissed. If the list cannot be ' +
    'read, this card says so instead of saying nothing is leaking.',
};

const SALES_RETURNS: TourStep = {
  route: '/sales',
  anchor: 'sales-returns',
  title: 'What is coming back',
  body:
    'The returns and credit notes on the Sales page — how much value is coming back, from which ' +
    'customers, categories and products, and whether it is rising against the previous three ' +
    'months. Built from invoice data, so it lags live trading; a return rate is left blank ' +
    'rather than shown as zero when there are no sales to compare it against.',
};

/* SPEC-475 — the Sales page, one step per tab: what the tab is for and its first action. Each
 * anchor is the tab's intro line (`TabIntro`), which renders only on that tab — so a step skips
 * when its tab is not on screen. Exec language: results, not method. */
// SPEC-565 — the Overview tab: invoiced sales on one definition, beside QlikView. The anchor is
// the tab's intro line, so the step skips when the tab is not on screen.
const SALES_TAB_OVERVIEW: TourStep = {
  route: '/sales?tab=overview',
  anchor: 'sales-tab-overview',
  title: 'Sales at a glance, and how each figure is made',
  body:
    'Invoiced sales for last week, month to date and year to date, each beside the same window ' +
    'last year, and a full-year projection that is a run rate, not the forecast. The line above ' +
    'the figures says how up to date the invoices are. Hover the ⓘ on a figure to see how it is ' +
    'made. Below, split the year by channel, brand, account or category, and set a figure from ' +
    'QlikView beside ours to see the difference.',
};

const SALES_TAB_ALL: TourStep = {
  route: '/sales?tab=all',
  anchor: 'sales-tab-all',
  title: 'Your biggest customers, at a glance',
  body:
    'The largest customers by lifetime order value, with their rep, channel and credit limit. ' +
    'Search by name, code, rep or channel, then select a customer: their brief opens in the side panel — ' +
    'top products, credit position and a suggested next step.',
};

const SALES_TAB_AT_RISK: TourStep = {
  route: '/sales?tab=at-risk',
  anchor: 'sales-tab-at-risk',
  title: 'Customers who are late to order',
  body:
    'Customers who have gone 1.5 times longer than their usual gap without ordering. Sort by ' +
    'customer value to put the most valuable first, then select one to draft an email or copy ' +
    'a brief for the rep.',
};

const SALES_TAB_DORMANT: TourStep = {
  route: '/sales?tab=dormant',
  anchor: 'sales-tab-dormant',
  title: 'The win-back list',
  body:
    'Live accounts with no order for over 60 days and at least 3 past orders. Select one to see ' +
    'what they used to buy and draft a win-back email.',
};

const SALES_TAB_PUSH: TourStep = {
  route: '/sales?tab=push',
  anchor: 'sales-tab-push',
  title: 'Stock to sell, and who to offer it to',
  body:
    'Products holding more stock than their target cover, each with the customers who have ' +
    'bought it before. Excess is measured against the forecast, as the line under the heading ' +
    'says. Use Draft to write to a customer.',
};

const SALES_TAB_PIPELINE: TourStep = {
  route: '/sales?tab=pipeline',
  anchor: 'sales-tab-pipeline',
  title: 'Proformas that turned into sales',
  body:
    'Proforma orders since the start of last year for the 200 largest customers, and how many ' +
    'were invoiced within 60 days. The converted and pending £ are estimates, and the tiles say so. Select a customer to see ' +
    'their proformas in the side panel.',
};

const SALES_TAB_BUDGET: TourStep = {
  route: '/sales?tab=budget',
  anchor: 'sales-tab-budget',
  title: 'Where we stand against budget',
  body:
    'Invoiced value so far this year against each customer’s budget. Start with the gap ' +
    'tile; the customers furthest behind are at the top of the table.',
};

const SALES_TAB_REPS: TourStep = {
  route: '/sales?tab=reps',
  anchor: 'sales-tab-reps',
  title: 'Each rep’s book',
  body:
    'Each rep’s accounts, how many are at risk or dormant, and how much of the book sits ' +
    'with their top three customers. Select a rep to see the accounts to call.',
};

const SALES_TAB_PROMO: TourStep = {
  route: '/sales?tab=promo',
  anchor: 'sales-tab-promo',
  title: 'Products selling faster or slower than usual',
  body:
    'Products whose rate of sale over the last 8 weeks moved sharply against the last 24 weeks. ' +
    'A spike can be a promotion, a new listing or a one-off order, so check before acting.',
};

const SALES_TAB_CATEGORIES: TourStep = {
  route: '/sales?tab=categories',
  anchor: 'sales-tab-categories',
  title: 'Which categories earn the most',
  body:
    'Invoiced value and margin by category in the date range. Margin counts only the invoice ' +
    'lines that carry a cost; the line under the table says how many do.',
};

// SPEC-417d — Deductions
const DEDUCTIONS_OVERVIEW: TourStep = {
  route: '/deductions',
  anchor: 'deductions',
  title: 'Promotional deductions',
  body:
    'A retailer runs a promotion on our product, funds part of the discount, and charges the ' +
    'rest back to us. This page starts with our own side of the ledger: for each promotion, ' +
    'what we actually sold at or near the promo price, and the discount we carried.',
};

const DEDUCTIONS_AWAITING: TourStep = {
  route: '/deductions',
  anchor: 'deductions-awaiting',
  title: 'What is still to come',
  body:
    'Checking each retailer deduction is fair needs the deductions feed, the agreed funding ' +
    'terms and EPOS sell-through. Those are not connected yet, so this panel names what it is ' +
    'waiting on rather than showing made-up numbers. Funding exposure stays blank until a real ' +
    'rate is on file.',
};

/* ── SPEC-477: Supplier Risk, tab by tab ───────────────────────────────────
 * Each tab step spotlights the tab's intro line (`supplier-tab-<key>`). The row-detail step
 * spotlights the side-panel detail, which exists only once a supplier is selected — with
 * nothing selected the step skips rather than pointing at an empty panel (SPEC-145). */
const SUPPLIER_SUMMARY: TourStep = {
  route: '/supplier-risk?tab=overview',
  anchor: 'supplier-summary',
  title: 'Supplier risk in four counts',
  body:
    'How many suppliers are on record, how many open order lines are past their ETA, how many ' +
    'products have only one supplier, and how many suppliers have more than two late lines. ' +
    'Each count has an ⓘ that says how it is worked out. A count that could not be read says so.',
};

const SUPPLIER_TAB_OVERVIEW: TourStep = {
  route: '/supplier-risk?tab=overview',
  anchor: 'supplier-tab-overview',
  title: 'Every supplier, most late lines first',
  body:
    'One row per supplier. Open and late lines come from the live open-order book; the order ' +
    'count and the promised lead time come from the older purchase-order record. Select a ' +
    'supplier: its detail opens in the side panel.',
};

const SUPPLIER_ROW_DETAIL: TourStep = {
  route: '/supplier-risk?tab=overview',
  anchor: 'supplier-row-detail',
  title: 'One supplier, in the side panel',
  body:
    'The selected supplier: its open lines, its promised lead time (average, shortest and ' +
    'longest), how many of its earliest open lines are past their ETA, and the products it ' +
    'supplies with their cover. It ends with what to do next.',
};

const SUPPLIER_TAB_OVERDUE: TourStep = {
  route: '/supplier-risk?tab=overdue',
  anchor: 'supplier-tab-overdue',
  title: 'Order lines past their ETA',
  body:
    'Every open order line with cases still to arrive whose ETA has passed, most days late ' +
    'first. Search by supplier or product, then chase the supplier for a revised ETA.',
};

const SUPPLIER_TAB_LEAD_TIMES: TourStep = {
  route: '/supplier-risk?tab=lead-times',
  anchor: 'supplier-tab-lead-times',
  title: 'The lead time planning uses',
  body:
    'The lead time of record for each product — the one planning uses — with the measured lead ' +
    'time from completed orders beside it as a check. The planning team can amend the lead ' +
    'time of record (the Amend button shows only for them); an amendment is marked, and Revert goes ' +
    'back to the master figure.',
};

const SUPPLIER_TAB_SOLE_SOURCE: TourStep = {
  route: '/supplier-risk?tab=sole-source',
  anchor: 'supplier-tab-sole-source',
  title: 'Products with only one supplier',
  body:
    'Products only one supplier has ever supplied, lowest cover first (no cover last). Cover here is live ' +
    'warehouse stock at the 8-week order rate, not the forecast — the line above the table ' +
    'says so.',
};

const SUPPLIER_TAB_ORIGINS: TourStep = {
  route: '/supplier-risk?tab=origins',
  anchor: 'supplier-tab-origins',
  title: 'Where the open orders come from',
  body:
    'The open-order book by country: suppliers, order value, promised lead time and any ' +
    'weather watch. Value is shown in pounds only where an approved exchange rate exists; ' +
    'otherwise per currency. Select a country to see its main products in the side panel.',
};

/* ── SPEC-477: Products — the list, the counts above it, then one product ──── */
const PRODUCTS_MOVERS: TourStep = {
  route: '/products?tab=products',
  anchor: 'products-movers',
  title: 'Fast, medium, slow and dead movers',
  body:
    'Each product is ranked against its own category by its 8-week order rate. Dead means ' +
    'orders in the last 24 weeks but none in the last 8. The ⓘ on each tile explains the ranking.',
};

const PRODUCTS_TREND_CHIPS: TourStep = {
  route: '/products?tab=products',
  anchor: 'products-trend-chips',
  title: 'Speeding up or slowing down',
  body:
    'How many products are ordered more than 15% faster, or slower, over the last 8 weeks than ' +
    'over the last 12 — and how many are steady.',
};

const PRODUCTS_LIST: TourStep = {
  route: '/products?tab=products',
  anchor: 'products-tab-products',
  title: 'The product list',
  body:
    'The 200 fastest-moving products with their cases a week, trend and mover ranking. Search looks ' +
    'within these 200. A figure the product has no data for shows a grey label that says why. ' +
    'Select a product to open it in the side panel.',
};

const PRODUCT_ROW_DETAIL: TourStep = {
  route: '/products?tab=products',
  anchor: 'product-row-detail',
  title: 'One product, in the side panel',
  body:
    'The selected product: live warehouse stock, cover at the 8-week order rate, its stockout ' +
    'verdict and its order rates — the same blocks as its full page. The link at the bottom ' +
    'opens the full product page.',
};

const PRODUCTS_MARGINS: TourStep = {
  route: '/products?tab=margins',
  anchor: 'products-tab-margins',
  title: 'Margin by product',
  body:
    'Margin for the latest month and averaged back 3 and 12 months. An invoice line keeps its own ' +
    'cost; a line without one takes the landed cost per case from the order feed. A line with ' +
    'neither is left out, never guessed. The "Cost from" column names the source for each product. ' +
    'This margin is before promotion rebates and warehouse costs. ' +
    'Select a product to see its month-by-month margin, and how many lines carry a cost, in the ' +
    'side panel.',
};

/* ── SPEC-476: Supply Chain, tab by tab ───────────────────────────────────
 * Each tab step spotlights the tab's intro line (`sc-tab-<key>`) and names its first action.
 * The row-detail step spotlights the side-panel detail, which exists only once a product is
 * selected — with nothing selected the step skips rather than pointing at an empty panel
 * (SPEC-145). The Open POs and Transfers steps of the Buying Loop are reused as they are. */
const SC_SUMMARY: TourStep = {
  route: '/supply-chain?tab=stockout',
  anchor: 'sc-summary',
  title: 'Supply chain in four counts',
  body:
    'How many products are out of stock now, how many are below the cover floor, how many are ' +
    'overstocked, and how many order lines are still to arrive. Each count has an ⓘ that says how ' +
    'it is worked out. A count that could not be read says so.',
};

const SC_TAB_STOCKOUT: TourStep = {
  route: '/supply-chain?tab=stockout',
  anchor: 'sc-tab-stockout',
  title: 'Products that need a buy',
  body:
    'Out of stock now first, then the lowest cover. Stock is the live warehouse figure; demand is ' +
    'the forecast of record, as the line above the table says. Select a product: why it is at ' +
    'risk opens in the side panel.',
};

const SC_ROW_DETAIL: TourStep = {
  route: '/supply-chain?tab=stockout',
  anchor: 'sc-row-detail',
  title: 'One product, in the side panel',
  body:
    'The selected product: its stock, its demand of record and its cover, an explanation of the ' +
    'risk, and its order history as a check beside the forecast. It ends with a link to the full ' +
    'product page.',
};

const SC_TAB_OVERSTOCK: TourStep = {
  route: '/supply-chain?tab=overstock',
  anchor: 'sc-tab-overstock',
  title: 'Products with more stock than they need',
  body:
    'Products whose cover is above the healthy ceiling, most excess cases first. Start with the ' +
    'top rows: they tie up the most stock.',
};

const SC_TAB_POS: TourStep = {
  route: '/supply-chain?tab=pos',
  anchor: 'sc-tab-pos',
  title: 'Every order line still to arrive',
  body:
    'One row per order line with cases still to arrive, earliest ETA first. Search by product, ' +
    'supplier or country, then select a line for its buyer, shipping and cost in the side panel.',
};

const SC_TAB_COVERAGE: TourStep = {
  route: '/supply-chain?tab=coverage',
  anchor: 'sc-tab-coverage',
  title: 'How long the stock lasts',
  body:
    'Every planned product with its stock, its demand of record and its cover, lowest cover ' +
    'first. A cover that cannot be worked out says why.',
};

const SC_TAB_SEASONAL: TourStep = {
  route: '/supply-chain?tab=seasonal',
  anchor: 'sc-tab-seasonal',
  title: 'When each category peaks',
  body:
    'The heatmap shows each category\'s busy months from invoice history. The table compares the ' +
    'same weeks last year with stock today — last year\'s invoices, not the forecast, as the line ' +
    'above it says.',
};

/* ── SPEC-476: Replenishment Signal Detail — the first action, then the decision ───
 * The decision step spotlights the Decision card, which exists only once a product is
 * selected — with nothing selected it skips (SPEC-145). */
const REPLENISHMENT_DETAIL_PICK: TourStep = {
  route: '/replenishment-detail',
  anchor: 'replenishment-picker',
  title: 'Start by finding a product',
  body:
    'Type a product code or name and pick it from the list. Its reorder decision, the inputs ' +
    'behind it and the reasoning then open below.',
};

const REPLENISHMENT_DETAIL_DECISION: TourStep = {
  route: '/replenishment-detail',
  anchor: 'replenishment-decision',
  title: 'The decision, step by step',
  body:
    'The decision gate, the stockout risk, and how the order quantity moves from suggested to ' +
    'final. Each figure has an ⓘ that says how it is worked out; a figure the run did not ' +
    'record says so.',
};

/* SPEC-537 — the lead-time check, always on the page below the decision. */
const REPLENISHMENT_DETAIL_LEAD_TIMES: TourStep = {
  route: '/replenishment-detail',
  anchor: 'lead-time-check',
  title: 'Lines with no lead time',
  body:
    'The served lines with no lead time, and the time from order to ETA on recent completed ' +
    'orders. The ETA is the planned arrival, not the receipt. Where there are 3 orders or more, ' +
    'Apply sets the measured figure as the lead time of record, one line at a time.',
};

/* ── The tours ────────────────────────────────────────────────────────────── */

export const TOURS: Tour[] = [
  {
    // SPEC-474: the Demand Planning page, one step per tab — each tab's first action.
    id: 'demand-planning-tabs',
    glyph: 'demand-planning-tabs',
    title: 'Demand Planning, Tab by Tab',
    audience: 'Every Demand Planning tab in order — what each one is for and the first thing to do on it.',
    steps: [MORNING_VIEW, DP_DEMAND_BASIS, FORECAST_WORKING_SET, FORECAST_DRILL, FORECAST_LOCK_SCORE,
      FORECAST_CLASSIFICATION, DP_CALCULATIONS, DP_AMENDMENTS, DEMAND_REQUESTS, SC_PROMO_CAL,
      DP_SCENARIOS, DP_SCENARIO_ASSUMPTIONS, DP_SCENARIO_TO_REQUEST, DP_SCENARIO_SUGGEST,
      DP_SCENARIO_MY_REQUESTS, TEAM_PSR_OPEN_A_PRODUCT, TEAM_PO_APPROVAL, DP_ROW_DETAIL, TEAM_STOCK_COVER,
      DP_DEFICIT, BBE_RISK, TEAM_WAREHOUSE, DP_PRODUCTION, PILOT_SCORECARD, DP_PIPELINE_RUNS],
  },
  {
    // SPEC-475: the Sales page, one step per tab — each tab's first action, then returns.
    id: 'sales-tabs',
    glyph: 'sales-tabs',
    title: 'Sales, Tab by Tab',
    audience: 'Every Sales tab in order — what each one is for and the first thing to do on it.',
    steps: [SALES_TAB_OVERVIEW, SALES_TAB_ALL, SALES_TAB_AT_RISK, SALES_TAB_DORMANT, SALES_TAB_PUSH, SALES_TAB_PIPELINE,
      SALES_TAB_BUDGET, SALES_TAB_REPS, SALES_TAB_PROMO, SALES_TAB_CATEGORIES, SALES_RETURNS],
  },
  {
    // SPEC-477: the Supplier Risk page, one step per tab, plus the summary and the side panel.
    id: 'supplier-risk-tabs',
    glyph: 'supplier-risk-tabs',
    title: 'Supplier Risk, Tab by Tab',
    audience: 'Every Supplier Risk tab in order — late lines, lead times, single-supplier products and where the orders come from.',
    steps: [SUPPLIER_SUMMARY, SUPPLIER_TAB_OVERVIEW, SUPPLIER_ROW_DETAIL, SUPPLIER_TAB_OVERDUE,
      SUPPLIER_TAB_LEAD_TIMES, SUPPLIER_TAB_SOLE_SOURCE, SUPPLIER_TAB_ORIGINS],
  },
  {
    // SPEC-477: the Products page — the counts, the list, one product, then margin.
    id: 'products',
    glyph: 'products',
    title: 'Products',
    audience: 'What is moving and what is not — the mover counts, the product list, one product in the side panel, and margin by product.',
    steps: [PRODUCTS_MOVERS, PRODUCTS_TREND_CHIPS, PRODUCTS_LIST, PRODUCT_ROW_DETAIL, PRODUCTS_MARGINS],
  },
  {
    // SPEC-476: the Supply Chain page, one step per tab, plus the summary and the side panel.
    id: 'supply-chain-tabs',
    glyph: 'supply-chain-tabs',
    title: 'Supply Chain, Tab by Tab',
    audience: 'Every Supply Chain tab in order — what needs a buy, what is overstocked, what is on the way, how long stock lasts, the seasons and internal moves.',
    steps: [SC_SUMMARY, SC_TAB_STOCKOUT, SC_ROW_DETAIL, SC_TAB_OVERSTOCK, SC_TAB_POS, TEAM_PO_ETA_SOURCES,
      SC_TAB_COVERAGE, SC_TAB_SEASONAL, TEAM_TRANSFERS],
  },
  {
    id: 'morning-loop',
    glyph: 'morning-loop',
    title: 'The Morning Loop',
    audience: "A demand planner's day — fresh data, today's worklist, forecasts, the board.",
    steps: [APP_RAIL, NAV_FIND_PAGE, NAV_CUSTOMISE, FEEDBACK_PILL, SUDHIN_ACTION_BOARD, FORECAST_WATCHLIST, MORNING_VIEW, THIS_WEEK, FRESHNESS, DATA_SOURCE_INDICATOR, WORKLIST, DEMAND_REQUESTS, FORECAST_DRILL, FINAL_FORECAST_EVERY_LINE, FINAL_FORECAST_LINE_DETAIL, FINAL_FORECAST_TEAM_UPLOAD, THREE_FORECASTS, THREE_FORECASTS_OUTSIDE, THREE_FORECASTS_PRODUCTS, WITH_PROMOTIONS, WITH_PROMOTIONS_ACCURACY, FORECAST_GRID_SCOPE, ACCURACY_TWO_MEASURES, ACCURACY_BENCHMARKS, ACCURACY_WITH_PROMOTIONS, FORECAST_STOCK, FORECAST_CONTROLS, FORECAST_PERIOD_AMEND, BBE_RISK, PILOT_SCORECARD, DECISIONS_DESK],
  },
  {
    id: 'forecasting-deep-dive',
    glyph: 'forecasting-deep-dive',
    title: 'How the Forecast Is Made',
    audience: 'The forecasting engine end to end — working set, demand class, the model tournament, and how each week is scored.',
    steps: [FORECAST_WORKING_SET, FORECAST_CLASSIFICATION, FORECAST_DRILL, FINAL_FORECAST_EVERY_LINE, FINAL_FORECAST_LINE_DETAIL, FINAL_FORECAST_TEAM_UPLOAD, THREE_FORECASTS, THREE_FORECASTS_OUTSIDE, THREE_FORECASTS_PRODUCTS, WITH_PROMOTIONS, WITH_PROMOTIONS_ACCURACY, FORECAST_GRID_SCOPE, ACCURACY_TWO_MEASURES, ACCURACY_BENCHMARKS, ACCURACY_WITH_PROMOTIONS, FORECAST_STOCK, FORECAST_REGIME, FORECAST_CONTROLS, FORECAST_HISTORY, REGIME_MEASUREMENT, FORECAST_LOCK_SCORE, FVA_BUYING_DECISIONS, SERVICE_FILL_RATE, FORECAST_PERIOD_AMEND, PILOT_SCORECARD],
  },
  {
    id: 'meet-the-agents',
    glyph: 'meet-the-agents',
    title: 'Meet the Agents',
    audience: 'The watchers that run over the live data on their own.',
    steps: [ALERTS_TABS, AGENTS_PANEL, ALERTS_MONEY, ALERTS_DISMISS, ALERTS_THUMBS,
      ALERTS_SCOREBOARD, PRIORITY_TOP_VALUE, AGENTS_ARMED, ALERTS_UPDATES, ASK_AGENTS],
  },
  {
    id: 'ask-anything',
    glyph: 'ask-anything',
    title: 'Ask Anything',
    audience: 'The reports analyst — plain-English questions, cited answers, honest freshness.',
    steps: [REPORTS_STALENESS, REPORTS_PACK, REPORTS_NLQ, REPORTS_ORDER_MARGIN, REPORTS_DOWNLOAD, REPORTS_SAVED,
            REPORTS_EXEC_WEEKS, REPORTS_EXEC_TRENDS],
  },
  {
    id: 'route-intelligence',
    glyph: 'route-intelligence',
    title: 'Route Intelligence',
    audience: 'Live orders grouped into delivery areas — and what Phase 2 needs to auto-plan.',
    steps: [ROUTE_ORDER_POOL, ROUTE_MAP, ROUTE_LIVE, ROUTE_READINESS],
  },
  {
    // SPEC-542: Intelligence → Market News.
    id: 'market-news',
    glyph: 'market-news',
    title: 'Market News',
    audience: 'The news that touches our categories and freight — an overview of each story, its source, and earlier weeks.',
    steps: [MARKET_NEWS_FEED, MARKET_NEWS_WEEKS],
  },
  {
    id: 'trust-and-governance',
    glyph: 'trust-and-governance',
    title: 'Trust & Governance',
    audience: 'The pipeline and its health, feed freshness, the sign-off framework, policy switches, the audit trail, what changed, and the record of every suggestion and how it turned out.',
    // SPEC-448: the five observability steps run first, then the Method and Policy steps
    // — so the walk crosses pages once, not three times. SPEC-486 added the Changelog step.
    steps: [GOV_DATA_FLOW, CONTEXT_PANEL, GOV_READINESS, GOV_FRESHNESS, GOV_SENTINELS, GOV_WAREHOUSE_GAP, METHOD_PHASE_SIGNOFF, GOV_GUARDRAILS, GOV_AUDIT, METHOD_CHANGELOG,
      // SPEC-541: the record of the platform's own advice — the last stop on the trust walk.
      REC_TREND, REC_LINES, REC_PROPOSALS],
  },
  {
    id: 'buying-loop',
    glyph: 'buying-loop',
    title: 'The Buying Loop',
    audience: "The supply-chain team's day — have/need/act on one page, what is waiting approval, where the stock is, and what is short-dated.",
    steps: [TEAM_MORNING, TEAM_WORKLIST, TEAM_PSR_CONSOLE, TEAM_PSR_SCREENS, TEAM_PSR_SCOPE, TEAM_PSR_SELL_DOWN, TEAM_PSR_SEARCH, TEAM_PSR_PLAN_BY, TEAM_PSR_WORKINGS_EXPORT, TEAM_PSR_OPEN_A_PRODUCT, TEAM_PSR_NEXT_ACTIONS, TEAM_PSR_ORDERS_RAISED, TEAM_PSR_ACTION_SUGGESTIONS, TEAM_PSR_INBOUND_ETA, TEAM_PSR_SUPPLY_OVERRIDES, TEAM_PSR_AUDIT, TEAM_PSR_MARGIN_AT_RISK, TEAM_PSR_SHELF_LIFE_STEP, TEAM_PSR_BATCH_LIFE, TEAM_PSR_ECONOMIC_STEP, TEAM_PSR_CUSTOMER_MIN_LIFE, TEAM_PO_APPROVAL, TEAM_PO_ORDERS_RAISED, TEAM_STOCK_COVER, TEAM_WAREHOUSE, TEAM_PO_ETA_SOURCES, TEAM_TRANSFERS, BBE_RISK, DECISIONS_DESK],
  },
  {
    id: 'exec-risk-snapshot',
    hiddenForRoles: ['team', 'sc_director'],  // SPEC-255: sc_director is a non-exec working tier (mirrors exec_risk.py)
    glyph: 'exec-risk-snapshot',
    title: 'The Daily Risk Snapshot',
    audience: 'The Executive view — where the problems are today, in pounds: out of stock, running short, waiting on a decision, delayed.',
    steps: [RISK_SNAPSHOT, RISK_STOCK, RISK_GAPS, RISK_SUPPLIERS, REC_EXEC_TILE, WHO_WAS_CLOSER_TILE, EXEC_MARGIN_MONTH, DECISIONS_DESK],
  },
  {
    id: 'supply-chain-overview',
    glyph: 'supply-chain-overview',
    title: 'The Supply-Chain Overview',
    audience: 'The supply-chain team — out of stock ahead, forecast quality, promotions and what needs a buy, on one screen.',
    steps: [SC_OVERVIEW, SC_OOS_BANDS, SC_FORECAST_QUALITY, SC_PROMO_CAL, SC_PROMO_BY_SKU, SC_PLANNING, DECISIONS_DESK],
  },
  {
    id: 'analogue-matching',
    glyph: 'analogue-matching',
    title: 'New-Product Matching',
    audience: 'How a brand-new product gets a forecast — the closest existing products it borrows from, why each one matched, and how to choose a different match.',
    steps: [ANALOGUE_OVERVIEW, ANALOGUE_PREVIEW, ANALOGUE_CHOOSE_MATCH],
  },
  {
    id: 'material-requirements',
    glyph: 'material-requirements',
    title: 'Material Planning Console',
    audience: 'One raw material at a time — usage, netting and schedule in one place, with an honest note wherever a feed is not yet connected.',
    steps: [MRP_OVERVIEW, MRP_HONESTY],
  },
  {
    id: 'production-upload',
    glyph: 'production-upload',
    title: 'Production Upload',
    audience: 'How production files the weekly build plan — and reads the row-by-row check of what was accepted and what needs fixing.',
    steps: [PRODUCTION_UPLOAD_OVERVIEW, PRODUCTION_UPLOAD_LATEST],
  },
  {
    id: 'stock-targets',
    glyph: 'stock-targets',
    title: 'Stock Targets',
    audience: 'Whether each product’s stock cover is in band, below the band (buy) or above it (overstock) — the summary and the per-product table.',
    steps: [STOCK_TARGETS_OVERVIEW, STOCK_TARGETS_BANDS],
  },
  {
    id: 'forecast-benchmark',
    glyph: 'forecast-benchmark',
    title: 'Forecast Benchmark',
    audience: 'Does our forecast stand up against the purchasing system’s own buy report? The two, side by side, product by product.',
    steps: [FORECAST_BENCHMARK_OVERVIEW, FORECAST_BENCHMARK_HEADLINE],
  },
  {
    id: 'replenishment-detail',
    glyph: 'replenishment-detail',
    title: 'Replenishment Signal Detail',
    audience: 'The reorder decision for one product — inputs, calculation and reasoning.',
    steps: [REPLENISHMENT_DETAIL_OVERVIEW, REPLENISHMENT_DETAIL_PICK, REPLENISHMENT_DETAIL_DECISION,
      REPLENISHMENT_DETAIL_LEAD_TIMES],
  },
  {
    id: 'regime-alerts',
    glyph: 'regime-alerts',
    title: 'Regime Alerts',
    audience: 'Products whose demand pattern has shifted, and why each one sits in its state.',
    steps: [REGIME_ALERTS_OVERVIEW, REGIME_ALERTS_SUMMARY, REGIME_ALERTS_LIST],
  },
  {
    id: 'customer-dormancy',
    glyph: 'customer-dormancy',
    title: 'Customer Dormancy',
    audience: 'Customers who have gone quiet, the order value tied to them, and who is worth a call back.',
    steps: [CUSTOMER_DORMANCY_OVERVIEW, CUSTOMER_DORMANCY_SUMMARY, CUSTOMER_DORMANCY_LIST],
  },
  {
    id: 'retro-billing',
    glyph: 'retro-billing',
    title: 'Retro Billing',
    audience: 'Money that may be recoverable — credit-note leakage and absorbed supplier cost rises — and where each finding stands.',
    steps: [RETRO_BILLING_OVERVIEW, RETRO_BILLING_SUMMARY, RETRO_BILLING_FINDINGS],
  },
  {
    id: 'deductions',
    glyph: 'deductions',
    title: 'Deductions',
    audience: 'Retailer promotional chargebacks — our participation ledger now, full reconciliation once the feeds land.',
    steps: [DEDUCTIONS_OVERVIEW, DEDUCTIONS_AWAITING],
  },
  {
    id: 'v7-promo-engine',
    glyph: 'v7-promo-engine',
    title: 'Box A and the Promotional Engine',
    audience: 'The published forward demand product by product, plus a read-only preview of the promotional forecast engine.',
    steps: [V7_BOX_A, V7_BOX_A_LINE_SUMMARY, V7_BOX_A_CONTEXT, V7_BOX_A_HERO, V7_BOX_A_ALLOCATION,
      V7_BOX_A_SHARE_BASIS, V7_BOX_A_CONTRIBUTIONS, V7_BOX_A_AMENDMENT, V7_BOX_A_FRESHNESS, V7_BOX_A_DECOMPOSITION,
      V7_BOX_A_LINES,
      V7_BOX_A_CHAIN, V7_BOX_A_DEMAND_BASIS, V7_BOX_A_STRIP, V7_WORKBENCH, V7_WORKBENCH_FEEDBACK,
      V7_WORKBENCH_BACKTEST, V7_WORKBENCH_QUALITY, V7_WORKBENCH_MISSES, V7_PROMOTIONS,
      V7_PROMOTIONS_ENGINE, V7_PROMOTIONS_GOVERNANCE,
      V7_PROMOTIONS_READINESS, V7_PROMOTIONS_UNTRACKED, V7_PROMOTIONS_REP_BENCHMARK, V7_PROMOTIONS_FILTERS, V7_PROMOTIONS_LIST,
      V7_PROMOTIONS_ORDER_LEADS,
      V7_PROMOTIONS_BY_EVENT,
      // SPEC-455 — the comparison that decides whether the book drives the rest of the
      // platform. It belongs at the END of this tour: it only makes sense once the reader
      // knows what the book is.
      DEMAND_SOURCE_STATE, DEMAND_SOURCE_FRESHNESS, DEMAND_SOURCE_TREND, DEMAND_SOURCE_CAUSES],
  },
  {
    // SPEC-503: the Docs page — what it is, how to find a guide, and what the badge means.
    id: 'docs',
    glyph: 'docs',
    title: 'The Docs Page',
    audience: 'Everyone — where to read how each screen works, and how to tell a guide is current.',
    steps: [DOCS_PAGE, DOCS_NAV, DOCS_VERIFIED, DOCS_WATCH],
  },
  {
    id: 'delivery-roadmap',
    glyph: 'delivery-roadmap',
    title: 'The Delivery Roadmap',
    audience: 'Everyone — what is done, being built, next, and what we are waiting on.',
    steps: [ROADMAP],
  },
  {
    id: 'full-demo',
    glyph: 'full-demo',
    title: 'Full Demo',
    audience: 'The whole platform in one pass — the guided walk for a first look.',
    steps: [
      APP_RAIL,
      NAV_FIND_PAGE,
      NAV_CUSTOMISE,
      HELP_BUTTON,
      FEEDBACK_PILL,
      MORNING_VIEW,
      THIS_WEEK,
      WORKLIST,
      FORECAST_DRILL,
      FORECAST_PERIOD_AMEND,
      PILOT_SCORECARD,
      RAIL_TOURS,
      RAIL_FEEDBACK,
      RAIL_ACCOUNT,
      ALERTS_TABS,
      ALERTS_UPDATES,
      AGENTS_PANEL,
      ASK_AGENTS,
      REPORTS_NLQ,
      SALES_RETURNS,
      ROUTE_ORDER_POOL,
      GOV_DATA_FLOW,
      CONTEXT_PANEL,
      METHOD_PHASE_SIGNOFF,
      GOV_GUARDRAILS,
    ],
  },
];

/* ── Role-aware visibility ─────────────────────────────────────────────────
 * A step is visible only if its page is in the user's allowed pages and, when
 * the step targets a tab, that tab is in the user's allowed tabs for that page.
 * Sidebar/top-bar anchors (route '/') are visible to everyone. This makes the
 * step counter (x of y) honest per role, on top of the engine's runtime skip. */

/** Parse a step's route into { page, tab } for visibility checks. */
export function stepTarget(step: TourStep): { page: string; tab: string | null } {
  const route = step.route || '/';
  const [path, query = ''] = route.split('?');
  const tab = new URLSearchParams(query).get('tab');
  return { page: path || '/', tab };
}

export interface TourViewer {
  pages?: string[] | null;
  tabs?: Record<string, string[]> | null;
  role?: string | null;
}

/** Is a page visible to this user? undefined pages = allow (backwards compatible). */
function pageVisible(user: TourViewer | null | undefined, page: string): boolean {
  const pages = user?.pages;
  if (!pages || pages.length === 0) return true;
  if (page === '/') return true; // overview / sidebar-anchored steps
  // Mirror the sidebar's active test: exact for '/', prefix otherwise.
  return pages.some((p) => (p === '/' ? page === '/' : page.startsWith(p)));
}

/** Is a tab visible to this user? user.tabs is optional and only restricts when present. */
function tabVisible(user: TourViewer | null | undefined, page: string, tab: string | null): boolean {
  if (!tab) return true;
  const allowed = user?.tabs?.[page];
  if (!allowed) return true; // no per-tab restriction configured for this page
  return allowed.includes(tab);
}

/** The steps of a tour this user can actually see, in order. */
export function visibleSteps(tour: Tour, user: TourViewer | null | undefined): TourStep[] {
  return tour.steps.filter((s) => {
    const { page, tab } = stepTarget(s);
    return pageVisible(user, page) && tabVisible(user, page, tab);
  });
}

/** Tours with at least one step this user can see (what the picker lists). */
export function availableTours(user: TourViewer | null | undefined): Tour[] {
  return TOURS.filter(
    (t) =>
      !(user?.role && t.hiddenForRoles?.includes(user.role)) &&
      visibleSteps(t, user).length > 0,
  );
}
